/**
 * Raid motion — retreat/cohesion, dodge goals, ring/slot, plant blend.
 * Phaser-free. Geometry helpers consumed by kit-interp + select.
 */
import { raidCatalog } from "../../data/raid-catalog";
import type { RaidClassId } from "../../data/raid-catalog";
import {
  clampPointToRect,
  insetRect,
  retreatBlockedAtEdge,
  retreatDirBiased,
  sampleThreatAt,
  segmentThreatCost,
  underPressure,
  projectTangentialSlotOnCircle,
  clampPointToAnnulus,
  type BodyCircle,
  type Rect2D,
} from "../../sim";
import type {
  RaidBoss,
  RaidDanger,
  RaidTelegraph,
  RaidUnit,
} from "./raid-types";
import { raidFreezeDiag } from "./raid-freeze-diag";
import {
  RAID_AGGRO_BOSS_ID,
  raidAggroSticky,
} from "./raid-aggro";
import {
  biasSeatTowardHealerCover,
  encounterPackChargeForesightActive,
  getSoftAaIsolateFlags,
  judgmentNorm,
  judgmentOf,
  livingHealers,
  pointInAnyHealerStaff,
  seatStaffCoverScoreBonus,
  softStackMultiRoseMul,
  softStackSurviveSpeedMul,
  survivalSeekSeatBonus,
  unitIsStaffHealer,
} from "./judgment";
import { instinctAllowsSeatBreak } from "./judgment/polar";
import { instinctOf } from "./raid-disposition";
import {
  activeDangerZoneAt,
  collectRaidDangerZones,
  chargeCorridorActive,
  clearOfEnemyAttack,
  clearOfPackChewAttack,
  effectiveAaClearPadPx,
  enemyHitReach,
  firstEnteredDangerZoneOnSegment,
  pointClearsAllDangerZones,
  pointClearsDangerZone,
  pointInAnyDangerZone,
  pointInDangerZone,
  projectClearOfDangerZones,
  raidThreatMaxAcceptCost,
  raidThreatSegmentSteps,
  raidThreatTangentialOffsets,
  buildLocalHostileVolumes,
  raidThreatVolumes,
  unitInDangerZones,
  type RaidDangerZone,
} from "./raid-threat";
import {
  RaidEnemyTag,
  enemyHasTag,
  enemyIsCombatRelevant,
} from "./raid-enemy";
import { aaClearPadPxOf } from "./raid-disposition";
import { getActivePlayBounds } from "./sim-lab/sim-stage";
import {
  type AiIntent,
  type EngageTarget,
  dist,
  pickEngageTarget,
  hostileAttackReach,
  addAttackReach,
  resolveAddRadius,
  faceBoss,
  faceTarget,
  hostileHardMinSep,
  pickInWeaponReachAdd,
  engagementStyle,
} from "./raid-engage";
import {
  brassPadFactor,
  skillZoneMultiFloorApproachMul,
  skillZoneMultiFloorPadMul,
  skillZoneSoloPadMul,
  skillZoneSoloRadialOutBias,
  suppressAaGoalStealForSkillZones,
  zonesHaveLeapAndCharge,
  zonesHaveSoloLeap,
} from "./raid-skill-zone-policy";
import { topThreatTarget } from "../../sim";

export {
  brassMidHpKeepClearActive,
  brassPadFactor,
  brassSoftClearPadPx,
  zonesHaveBrass,
  zonesHaveLeapAndCharge,
  zonesHaveSoloLeap,
} from "./raid-skill-zone-policy";

const combatTable = () => raidCatalog.balance.combat;

// ── arena bounds (map table SSOT; raid-rules consumes this too) ────────────

export function raidPlayBounds(): Rect2D {
  return getActivePlayBounds();
}

/**
 * Wall-aware goal resolver: clamp into bounds; when the clamp eats most of
 * the intended step (wall-facing goal), slide along the free axis, then fall
 * back toward the arena center. Movement goals must never press the wall.
 */
export function resolveGoalInBounds(
  u: RaidUnit,
  tx: number,
  ty: number,
): { tx: number; ty: number } {
  const b = raidPlayBounds();
  const c = clampPointToRect(tx, ty, b, u.radius);
  const intended = dist(u.x, u.y, tx, ty);
  if (intended < 8) return { tx: c.x, ty: c.y };
  const actual = dist(u.x, u.y, c.x, c.y);
  if (actual >= intended * 0.4) return { tx: c.x, ty: c.y };
  // Slide: try each axis independently.
  const slideX = clampPointToRect(tx, u.y, b, u.radius);
  const slideY = clampPointToRect(u.x, ty, b, u.radius);
  const dX = dist(u.x, u.y, slideX.x, slideX.y);
  const dY = dist(u.x, u.y, slideY.x, slideY.y);
  if (Math.max(dX, dY) >= intended * 0.4) {
    return dX >= dY ? { tx: slideX.x, ty: slideX.y } : { tx: slideY.x, ty: slideY.y };
  }
  // Cornered: head toward arena center instead.
  const cx = (b.xMin + b.xMax) / 2;
  const cy = (b.yMin + b.yMax) / 2;
  const dc = dist(u.x, u.y, cx, cy) || 1;
  const step = Math.min(intended, 90);
  return {
    tx: u.x + ((cx - u.x) / dc) * step,
    ty: u.y + ((cy - u.y) / dc) * step,
  };
}

// ── geometry helpers (rings / slots / danger shapes) ───────────────────────

function gestureFormationKnobs(): {
  weight: number;
} {
  const g = (
    raidCatalog.balance as {
      gesture?: { formationAnchorWeight?: number };
    }
  ).gesture;
  const w = Number(g?.formationAnchorWeight);
  return {
    // Default high: user-presented destination should stick vs adjutant seed.
    weight: Number.isFinite(w) ? Math.min(1, Math.max(0, w)) : 0.95,
  };
}

/** Blend weight toward gestureAnchor while TTL holds (0..1). */
export function gestureFormationWeight(): number {
  return gestureFormationKnobs().weight;
}

/**
 * Active formation reference after dash/groupDash/pin.
 * Per-unit only: unit's own gestureAnchor while TTL holds (null otherwise).
 * Non-selected allies must not inherit another unit's/group center anchor.
 */
export function formationReference(
  u: RaidUnit,
  time: number,
): { x: number; y: number } | null {
  const ga = u.brain.gestureAnchor;
  if (ga && time < ga.until) return { x: ga.x, y: ga.y };
  return null;
}

/** Desired ring around boss from bearing origin, clamped into [minRing, maxRing]. */
/** Desired ring around a circle center from origin bearing. */
export function ringPointFromBearing(
  originX: number,
  originY: number,
  center: { x: number; y: number },
  ring: number,
  minRing: number,
  maxRing: number,
): { tx: number; ty: number } {
  let dx = originX - center.x;
  let dy = originY - center.y;
  let len = Math.hypot(dx, dy);
  if (len < 1) {
    dx = 0;
    dy = 1;
    len = 1;
  }
  const r = Math.min(maxRing, Math.max(minRing, ring));
  return {
    tx: center.x + (dx / len) * r,
    ty: center.y + (dy / len) * r,
  };
}

/** Desired ring around hostile from unit (or active formation-ref) bearing. */
export function ringPointFromUnit(
  u: RaidUnit,
  hostile: { x: number; y: number },
  ring: number,
  minRing: number,
  maxRing: number,
  opts?: { time?: number },
): { tx: number; ty: number } {
  const time = opts?.time;
  const ref = time != null ? formationReference(u, time) : null;
  const ox = ref?.x ?? u.x;
  const oy = ref?.y ?? u.y;
  return ringPointFromBearing(ox, oy, hostile, ring, minRing, maxRing);
}

/**
 * Pull a world goal onto the attackable annulus around a hostile circle
 * if formation slot pushed it outside reach.
 */
export function clampGoalToHostileRing(
  tx: number,
  ty: number,
  u: RaidUnit,
  hostile: BodyCircle,
  maxRing: number,
  minRing = hostileHardMinSep(u, hostile),
): { tx: number; ty: number } {
  const p = clampPointToAnnulus(
    tx,
    ty,
    hostile.x,
    hostile.y,
    minRing,
    maxRing,
    { x: u.x, y: u.y },
  );
  return { tx: p.x, ty: p.y };
}

/** Keep the goal inside weapon reach of a point hostile (add). */
export function clampGoalToAddReach(
  tx: number,
  ty: number,
  u: RaidUnit,
  target: EngageTarget,
  reach: number,
): { tx: number; ty: number } {
  const minD = (u.radius + 14) * 0.9;
  const maxD = reach * 0.96;
  const p = clampPointToAnnulus(tx, ty, target.x, target.y, minD, maxD, {
    x: u.x,
    y: u.y,
  });
  return { tx: p.x, ty: p.y };
}

export function allyInBrass(allies: readonly RaidUnit[], boss: RaidBoss): boolean {
  const tg = boss.telegraph;
  if (!tg || tg.remain <= 0) return false;
  for (const a of allies) {
    if (a.hp <= 0) continue;
    if (pointInTelegraph(a.x, a.y, boss)) return true;
  }
  return false;
}

/** Caster with optional telegraph (boss or elite brute). */
export type RaidTelegraphCaster = {
  x: number;
  y: number;
  telegraph: RaidTelegraph | null;
};

export function pointInTelegraph(
  x: number,
  y: number,
  caster: RaidTelegraphCaster,
): boolean {
  const tg = caster.telegraph;
  if (!tg) return false;
  if (tg.shape === "line") {
    const pad = Math.max(0, tg.originPad ?? 0);
    const ox = caster.x + Math.cos(tg.aimAngle) * pad;
    const oy = caster.y + Math.sin(tg.aimAngle) * pad;
    return pointInDangerZone(x, y, 0, {
      id: "tg",
      sourceId: "boss",
      shape: "line",
      x: ox,
      y: oy,
      range: tg.range,
      aimAngle: tg.aimAngle,
      halfWidth: tg.halfWidth,
      danger: tg.danger,
      skillId: tg.skillId,
    });
  }
  const d = dist(caster.x, caster.y, x, y);
  if (d > tg.range) return false;
  if (tg.shape === "circle") return true;
  const ang = Math.atan2(y - caster.y, x - caster.x);
  const aim = tg.aimAngle ?? -Math.PI / 2;
  let diff = ang - aim;
  // Non-finite aim would spin forever on ±Inf.
  if (!Number.isFinite(diff)) return false;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  const half = ((tg.coneDeg ?? 60) * Math.PI) / 180 / 2;
  return Math.abs(diff) <= half;
}

/**
 * Compat caster pose for tests / call sites that still pass brute casters.
 * Prefer `collectRaidDangerZones` + enemies; AI must not read leap.phase.
 */
export type RaidDangerCaster = {
  x: number;
  y: number;
  telegraph: RaidTelegraph | null;
  leap: import("./raid-types").RaidLeap | null;
  id?: number;
};

/** Merge optional caster extras (usually RaidUnit brutes) into DangerZones. */
function zonesWithExtras(
  boss: RaidBoss,
  extras?: readonly RaidDangerCaster[],
  includeBoss = true,
): RaidDangerZone[] {
  const enemies = (extras ?? []).filter(
    (e): e is RaidUnit => typeof (e as RaidUnit).id === "number",
  ) as RaidUnit[];
  return collectRaidDangerZones(boss, { enemies, includeBoss });
}

export function pointInBossDanger(
  x: number,
  y: number,
  unitRadius: number,
  boss: RaidBoss,
  extras?: readonly RaidDangerCaster[],
): boolean {
  return pointInAnyDangerZone(x, y, unitRadius, zonesWithExtras(boss, extras));
}

export function unitInBossDanger(
  u: RaidUnit,
  boss: RaidBoss,
  extras?: readonly RaidDangerCaster[],
): boolean {
  return pointInBossDanger(u.x, u.y, u.radius, boss, extras);
}

/** Living armed elites that can threaten allies with telegraph/leap. */
export function armedBruteCasters(
  enemies: readonly RaidUnit[],
): RaidUnit[] {
  return enemies.filter(
    (e) =>
      enemyIsCombatRelevant(e) &&
      enemyHasTag(e, RaidEnemyTag.Elite) &&
      (e.telegraph != null || e.leap != null),
  );
}

/** True when (x,y) clears all active DangerZones by at least `pad`. */
export function pointClearsBossDanger(
  x: number,
  y: number,
  unitRadius: number,
  boss: RaidBoss,
  pad: number,
  extras?: readonly RaidDangerCaster[],
): boolean {
  return pointClearsAllDangerZones(
    x,
    y,
    unitRadius,
    zonesWithExtras(boss, extras),
    pad,
  );
}

export function dodgePadPx(mul: number): number {
  return raidCatalog.balance.combat.brassDodgeDist * mul;
}

/**
 * Solo-leap escape bearing: circleDangerEscapeDir + optional radial-out
 * blend when feet have a stable radial from land center (len≥6).
 * Near-center lateral SSOT unchanged.
 */
export function soloLeapEscapeDir(
  u: RaidUnit,
  z: Pick<RaidDangerZone, "x" | "y" | "skillId">,
  approachFrom?: { x: number; y: number } | null,
): { dx: number; dy: number } {
  const base = circleDangerEscapeDir(u, z, approachFrom);
  const bias = skillZoneSoloRadialOutBias("leap");
  if (bias <= 0) return base;
  const rdx = u.x - z.x;
  const rdy = u.y - z.y;
  const rlen = Math.hypot(rdx, rdy);
  if (rlen < 6) return base;
  const rx = rdx / rlen;
  const ry = rdy / rlen;
  let bx = base.dx * (1 - bias) + rx * bias;
  let by = base.dy * (1 - bias) + ry * bias;
  const bl = Math.hypot(bx, by);
  if (bl < 1e-6) return base;
  return { dx: bx / bl, dy: by / bl };
}

/**
 * Preferred escape direction from a danger circle.
 * Default = **radial outward** (shortest path out of the hit disk).
 * Leap near pad center has no stable radial — use lateral to boss→land so
 * goals do not pile on the legacy south (+y) fallback under the telegraph.
 * Offset feet (lip / half-disk) keep radial: forced lateral skims a chord
 * through the disk and still gets hit if resolve lands mid-step.
 */
export function circleDangerEscapeDir(
  u: RaidUnit,
  z: Pick<RaidDangerZone, "x" | "y" | "skillId">,
  approachFrom?: { x: number; y: number } | null,
): { dx: number; dy: number } {
  const dx0 = u.x - z.x;
  const dy0 = u.y - z.y;
  const len = Math.hypot(dx0, dy0);
  // Unstable radial at pad center — leap picks lateral; other circles +x.
  if (len < 6 && z.skillId === "leap") {
    const from = approachFrom ?? { x: z.x, y: z.y - 100 };
    let ax = z.x - from.x;
    let ay = z.y - from.y;
    let alen = Math.hypot(ax, ay);
    if (alen < 1e-3) {
      ax = 0;
      ay = 1;
      alen = 1;
    }
    ax /= alen;
    ay /= alen;
    const lx = -ay;
    const ly = ax;
    const lat = dx0 * lx + dy0 * ly;
    let side: number;
    if (Math.abs(lat) >= 1) {
      side = lat >= 0 ? 1 : -1;
    } else {
      const b = raidPlayBounds();
      const midX = (b.xMin + b.xMax) * 0.5;
      const toMidLat = (midX - z.x) * lx;
      side = toMidLat >= 0 ? 1 : -1;
      if (Math.abs(toMidLat) < 1) side = u.x >= z.x ? 1 : -1;
    }
    return { dx: lx * side, dy: ly * side };
  }
  if (len < 1) return { dx: 1, dy: 0 };
  return { dx: dx0, dy: dy0 };
}

/** Preferred sidestep out of a single DangerZone (no bounds). */
export function dodgeZoneGoalRaw(
  u: RaidUnit,
  z: RaidDangerZone,
  mul: number,
  approachFrom?: { x: number; y: number } | null,
): { tx: number; ty: number } {
  const distPx = dodgePadPx(mul);
  if (z.shape === "circle") {
    const clearR = z.range + u.radius + distPx;
    const { dx, dy } = circleDangerEscapeDir(u, z, approachFrom);
    const len = Math.max(1e-3, Math.hypot(dx, dy));
    return {
      tx: z.x + (dx / len) * clearR,
      ty: z.y + (dy / len) * clearR,
    };
  }
  if (z.shape === "line") {
    const aim = z.aimAngle ?? 0;
    const fx = Math.cos(aim);
    const fy = Math.sin(aim);
    const lx = -fy;
    const ly = fx;
    const dx = u.x - z.x;
    const dy = u.y - z.y;
    const along = dx * fx + dy * fy;
    const lat = dx * lx + dy * ly;
    const side = lat >= 0 ? 1 : -1;
    const hw = Math.max(1, z.halfWidth ?? z.coneDeg ?? 20);
    const clearLat = hw + u.radius + distPx;
    const len = Math.max(1e-3, z.range);
    const alongClamped = Math.max(0, Math.min(len, along));
    return {
      tx: z.x + fx * alongClamped + lx * side * clearLat,
      ty: z.y + fy * alongClamped + ly * side * clearLat,
    };
  }
  const aim = z.aimAngle ?? -Math.PI / 2;
  const ang = Math.atan2(u.y - z.y, u.x - z.x);
  let diff = ang - aim;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  const side = diff >= 0 ? 1 : -1;
  const half = ((z.coneDeg ?? 60) * Math.PI) / 180 / 2;
  const brassF = brassPadFactor(u, z);
  const d = Math.max(40, dist(u.x, u.y, z.x, z.y));
  const bodyAng = Math.asin(
    Math.min(1, (u.radius + distPx * 0.25 * brassF) / d),
  );
  const outAng = aim + side * (half + bodyAng + 0.2);
  const outR = d + Math.max(distPx * 0.35 * brassF, u.radius);
  return {
    tx: z.x + Math.cos(outAng) * outR,
    ty: z.y + Math.sin(outAng) * outR,
  };
}

/**
 * Formation offset — deterministic per unit id only. (Peer-index versions
 * reshuffled every slot whenever a peer died, vibrating the whole ring.)
 */
export function formationOffset(
  u: RaidUnit,
  classId: RaidClassId,
): { ox: number; oy: number } {
  const slots = raidCatalog.balance.combat.formationSlotR;
  const slotR = slots[classId] ?? 32;
  const ang = ((u.id % 9) / 9) * Math.PI * 2 + (u.id % 7) * 0.17;
  return { ox: Math.cos(ang) * slotR, oy: Math.sin(ang) * slotR };
}

/** True when ally is a heal need under `belowPct` (1+ → any missing HP). */
export function allyNeedsHeal(a: RaidUnit, belowPct: number): boolean {
  if (a.hp <= 0) return false;
  if (belowPct >= 1) return a.hp < a.maxHp;
  return a.hp / a.maxHp < belowPct;
}

export function lowestAlly(
  allies: readonly RaidUnit[],
  belowPct: number,
): RaidUnit | null {
  let best: RaidUnit | null = null;
  let bestRatio = 1;
  for (const a of allies) {
    if (!allyNeedsHeal(a, belowPct)) continue;
    const r = a.hp / a.maxHp;
    if (r < bestRatio) {
      bestRatio = r;
      best = a;
    }
  }
  return best;
}

/** Allies below HP% threshold, lowest ratio first (stable id tiebreak). */
export function hurtAlliesSorted(
  allies: readonly RaidUnit[],
  belowPct: number,
): RaidUnit[] {
  const hurt: RaidUnit[] = [];
  for (const a of allies) {
    if (allyNeedsHeal(a, belowPct)) hurt.push(a);
  }
  hurt.sort((a, b) => {
    const ra = a.hp / a.maxHp;
    const rb = b.hp / b.maxHp;
    if (ra !== rb) return ra - rb;
    return a.id - b.id;
  });
  return hurt;
}

/**
 * Bond-gated heal triage: low `healSplitMul` piles on lowestAlly;
 * high mul assigns distinct hurt slots among living healers (id rank).
 * Approach / who-to-walk-toward only — never gate in-range casts.
 * Soft HP triage only (heal ownership sticky / Claim B chase banned).
 */
export function healSplitAlly(
  healer: RaidUnit,
  allies: readonly RaidUnit[],
  belowPct: number,
  healSplitMul: number,
): RaidUnit | null {
  const hurt = hurtAlliesSorted(allies, belowPct);
  if (hurt.length === 0) return null;
  const t = Math.max(0, Math.min(1, healSplitMul));
  if (hurt.length === 1 || t <= 0.05) return hurt[0]!;

  const healers = allies
    .filter((a) => unitIsStaffHealer(a))
    .sort((a, b) => a.id - b.id);
  let myIdx = healers.findIndex((h) => h.id === healer.id);
  if (myIdx < 0) myIdx = 0;

  const wrapIdx = myIdx % hurt.length;
  const slot = Math.round(wrapIdx * t);
  return hurt[Math.min(hurt.length - 1, Math.max(0, slot))]!;
}

/**
 * Cast target: most-injured ally already inside staff range.
 * Does not lock to healSplit slot — nearby need always wins over a far triage.
 * `belowPct >= 1` → heal until full (`hp < maxHp`); still prefer lowest ratio.
 * (Soft≪AA in-staff chew-bias always-on Soft-regressed N20 0.3→0.85 — rejected.)
 * Insight Soft assist (flag): healer clear + ally chewed in-staff → near-lowest prefer.
 */
export function pickInRangeHealNeed(
  healer: RaidUnit,
  allies: readonly RaidUnit[],
  belowPct: number,
  enemies: readonly RaidUnit[] = [],
): RaidUnit | null {
  const reach = healer.attackRange;
  let best: RaidUnit | null = null;
  let bestRatio = 1;
  for (const a of allies) {
    if (!allyNeedsHeal(a, belowPct)) continue;
    if (dist(healer.x, healer.y, a.x, a.y) >= reach) continue;
    const r = a.hp / a.maxHp;
    if (
      r < bestRatio ||
      (r === bestRatio && (best == null || a.id < best.id))
    ) {
      bestRatio = r;
      best = a;
    }
  }
  const soft = getSoftAaIsolateFlags();
  if (
    soft.insightChewTriageInStaff &&
    best &&
    enemies.length > 0 &&
    clearOfPackChewAttack(healer.x, healer.y, healer.radius, enemies, 2)
  ) {
    const insightT = judgmentNorm(judgmentOf(healer).insight);
    // 통찰 Soft assist — low insight skips (polar reserved).
    if (insightT > 0.05) {
      let chewBest: RaidUnit | null = null;
      let chewRatio = 1;
      for (const a of allies) {
        if (!allyNeedsHeal(a, belowPct)) continue;
        if (dist(healer.x, healer.y, a.x, a.y) >= reach) continue;
        if (clearOfPackChewAttack(a.x, a.y, a.radius, enemies, 2)) continue;
        const r = a.hp / a.maxHp;
        if (
          r < chewRatio ||
          (r === chewRatio && (chewBest == null || a.id < chewBest.id))
        ) {
          chewRatio = r;
          chewBest = a;
        }
      }
      // Near-lowest only (ε 0.08) — never override a much more critical ally.
      if (chewBest && chewRatio <= bestRatio + 0.08) {
        return chewBest;
      }
    }
  }
  return best;
}

export function frontTank(allies: readonly RaidUnit[]): RaidUnit | null {
  let best: RaidUnit | null = null;
  let bestY = Infinity;
  for (const a of allies) {
    if (a.hp <= 0 || a.classId !== "tank") continue;
    if (a.y < bestY) {
      bestY = a.y;
      best = a;
    }
  }
  return best;
}

export function applySlot(
  tx: number,
  ty: number,
  u: RaidUnit,
): { tx: number; ty: number } {
  const { ox, oy } = formationOffset(u, u.classId);
  return { tx: tx + ox, ty: ty + oy };
}

/**
 * Formation slot on a hostile circle: tangential spread then annulus clamp.
 * Cartesian offsets would push a middle melee unit radially past reach;
 * soft-sep then parks them in the arrive deadzone with wantAttack false.
 */
export function applySlotNearHostile(
  tx: number,
  ty: number,
  u: RaidUnit,
  hostile: BodyCircle,
  maxRing: number,
  opts?: { time?: number; minRing?: number },
): { tx: number; ty: number } {
  const time = opts?.time;
  const minRing =
    opts?.minRing ?? hostile.radius + u.radius + 8;
  const ref = time != null ? formationReference(u, time) : null;
  // Gesture formation ref: slot on the ring bearing from the ref (reach assist).
  const base = ref
    ? ringPointFromBearing(
        ref.x,
        ref.y,
        { x: hostile.x, y: hostile.y },
        maxRing,
        minRing,
        maxRing,
      )
    : { tx, ty };
  const { ox, oy } = formationOffset(u, u.classId);
  const slotted = projectTangentialSlotOnCircle(
    base.tx,
    base.ty,
    hostile.x,
    hostile.y,
    ox,
    oy,
    { x: u.x, y: u.y },
  );
  return clampGoalToHostileRing(
    slotted.x,
    slotted.y,
    u,
    hostile,
    maxRing,
    hostileHardMinSep(u, hostile),
  );
}

/**
 * Escape a danger circle to at least `clearRadius` from its center, staying
 * in bounds. Prefer full clearance; never accept a clamped point that is
 * still inside `minRadius` (hit volume) when a farther candidate exists.
 */
export function escapeCircleInBounds(
  u: RaidUnit,
  centerX: number,
  centerY: number,
  clearRadius: number,
  prefDirX: number,
  prefDirY: number,
  minRadius: number,
): { tx: number; ty: number } {
  const b = raidPlayBounds();
  const base = Math.atan2(prefDirY, prefDirX);
  // Prefer full clearRadius; if bounds clamp every candidate back inside the
  // hit disk, retry shorter rings so large dodgeDistMul cannot trap the goal.
  const radii = [
    clearRadius,
    (clearRadius + minRadius) * 0.5,
    minRadius + Math.max(8, (clearRadius - minRadius) * 0.25),
    minRadius + 4,
  ];
  let bestP: { x: number; y: number } | null = null;
  let bestD = -1;
  let bestClear: { x: number; y: number } | null = null;
  let bestClearD = -1;
  for (const ring of radii) {
    for (const off of [0, 0.6, -0.6, 1.2, -1.2, 1.8, -1.8, 2.4, -2.4, Math.PI]) {
      const ang = base + off;
      const px = centerX + Math.cos(ang) * ring;
      const py = centerY + Math.sin(ang) * ring;
      const c = clampPointToRect(px, py, b, u.radius);
      const d = dist(centerX, centerY, c.x, c.y);
      if (d >= clearRadius) return { tx: c.x, ty: c.y };
      if (d >= minRadius && d > bestClearD) {
        bestClearD = d;
        bestClear = c;
      }
      if (d > bestD) {
        bestD = d;
        bestP = c;
      }
    }
    // Full ring found a point outside the hit volume — stop shrinking.
    if (bestClear) break;
  }
  if (bestClear) return { tx: bestClear.x, ty: bestClear.y };
  return bestP ? { tx: bestP.x, ty: bestP.y } : { tx: u.x, ty: u.y };
}

/** Project a dodge goal out of every active DangerZone, then clamp to bounds.
 *  Role danger filter is for *trigger* only — goals always pad-clear all floors
 *  (tank red-dodge must not plant into yellow brass/charge). */
function finalizeDodgeGoal(
  u: RaidUnit,
  goal: { tx: number; ty: number } | null,
  zones: readonly RaidDangerZone[],
  pad: number,
  _danger?: RaidDanger,
): { tx: number; ty: number } | null {
  if (!goal) return null;
  const cleared = projectClearOfDangerZones(
    goal.tx,
    goal.ty,
    u.radius,
    zones,
    pad,
  );
  return resolveGoalInBounds(u, cleared.x, cleared.y);
}

/** Fixed retreat / pack-exit step — cohesion only biases direction, not length. */
const RETREAT_STEP_PX = 100;

/**
 * Push a goal past packChew hit reach + pad. Ignores elite/charger bodies so
 * ironhorn sidestep seats are not cancelled by nearby elites.
 * selfProtect 0 → pad 0 (AA eps only); no hidden ≥10 floor.
 */
export function pushGoalClearOfPackChewAttack(
  u: RaidUnit,
  goal: { tx: number; ty: number },
  enemies: readonly RaidUnit[],
  aaPad: number,
): { tx: number; ty: number } {
  const pad = Math.max(0, aaPad);
  const isClear = (x: number, y: number) =>
    clearOfPackChewAttack(x, y, u.radius, enemies, 2 + pad);
  if (isClear(goal.tx, goal.ty)) return goal;
  let tx = goal.tx;
  let ty = goal.ty;
  for (let i = 0; i < 6; i++) {
    if (isClear(tx, ty)) break;
    let nearest: RaidUnit | null = null;
    let nearestD = Infinity;
    for (const e of enemies) {
      if (!enemyIsCombatRelevant(e) || !e.combatArmed) continue;
      if (!enemyHasTag(e, RaidEnemyTag.PackChew)) continue;
      const d = Math.hypot(e.x - tx, e.y - ty);
      if (d < nearestD) {
        nearestD = d;
        nearest = e;
      }
    }
    if (!nearest) break;
    const want = enemyHitReach(nearest, u.radius) + 2 + pad;
    let dx = tx - nearest.x;
    let dy = ty - nearest.y;
    let len = Math.hypot(dx, dy);
    if (len < 1e-3) {
      dx = 0;
      dy = -1;
      len = 1;
    }
    dx /= len;
    dy /= len;
    tx = nearest.x + dx * want;
    ty = nearest.y + dy * want;
  }
  const radial = resolveGoalInBounds(u, tx, ty);
  if (isClear(radial.tx, radial.ty)) return radial;
  // Multi-chew: nearest-radial can bounce between bodies — fan from pack.
  const flee = packChewFleePoint(u, enemies);
  if (flee) return seatClearOfPackChew(u, flee, enemies, pad);
  return radial;
}

/** @deprecated Use {@link pushGoalClearOfPackChewAttack}. */
export const pushGoalClearOfThrallAttack = pushGoalClearOfPackChewAttack;

/**
 * Exit seat past a pack-chew cluster: bearings away from `fleeFrom` (pack
 * centroid / nearest chew), stepped out from the hero. Prefer clear AA.
 * Optional allies → dual-seat staff *filter* among clear seats (no pull).
 */
export function seatClearOfPackChew(
  u: RaidUnit,
  fleeFrom: { x: number; y: number },
  enemies: readonly RaidUnit[],
  aaPad: number,
  stepPx = RETREAT_STEP_PX,
  allies?: readonly RaidUnit[],
): { tx: number; ty: number } {
  const pad = Math.max(0, aaPad);
  const isClear = (x: number, y: number) =>
    clearOfPackChewAttack(x, y, u.radius, enemies, 2 + pad);
  let baseDx = u.x - fleeFrom.x;
  let baseDy = u.y - fleeFrom.y;
  let baseLen = Math.hypot(baseDx, baseDy);
  if (baseLen < 1e-3) {
    baseDx = 0;
    baseDy = -1;
    baseLen = 1;
  } else {
    baseDx /= baseLen;
    baseDy /= baseLen;
  }
  const angles = [0, 0.5, -0.5, 1, -1, 1.5, -1.5, 2, -2, 3, -3].map(
    (t) => t * (Math.PI / 4),
  );
  const steps =
    stepPx > RETREAT_STEP_PX * 1.05
      ? [stepPx, stepPx * 1.35, stepPx * 1.75, stepPx * 2.15]
      : [stepPx, stepPx * 1.35, stepPx * 1.75];
  const alliesLive = allies && allies.length > 0 ? allies : ([] as RaidUnit[]);
  const healers =
    alliesLive.length > 0 ? livingHealers(alliesLive) : ([] as RaidUnit[]);
  const j = judgmentOf(u);
  const insight = j.insight;
  const instinct = instinctOf(u);
  // Backline seed = individual score hint only (adjutant does not relocate).
  const tankHint = frontTank(alliesLive);
  const approachHints =
    tankHint && healers.length > 0
      ? healers.map((h) => {
          let fx = fleeFrom.x - tankHint.x;
          let fy = fleeFrom.y - tankHint.y;
          const len = Math.hypot(fx, fy) || 1;
          fx /= len;
          fy /= len;
          const back =
            (raidCatalog.balance.combat as { healerBackline?: number })
              .healerBackline ?? 55;
          return {
            unitId: h.id,
            seed: {
              x: tankHint.x - fx * back,
              y: tankHint.y - fy * back,
            },
          };
        })
      : [];
  let best: { tx: number; ty: number } | null = null;
  let bestScore = -Infinity;
  for (const step of steps) {
    for (const ang of angles) {
      const c = Math.cos(ang);
      const s = Math.sin(ang);
      const dx = baseDx * c - baseDy * s;
      const dy = baseDx * s + baseDy * c;
      const cand = resolveGoalInBounds(
        u,
        u.x + dx * step,
        u.y + dy * step,
      );
      const aaAt = countPackChewInAaAt(
        cand.tx,
        cand.ty,
        u.radius,
        enemies,
        2,
      );
      const clear = isClear(cand.tx, cand.ty);
      const dPack = Math.hypot(cand.tx - fleeFrom.x, cand.ty - fleeFrom.y);
      // Option A: Soft≪AA clear first; 통찰 survival-seek tip among clears.
      const seek = survivalSeekSeatBonus({
        x: cand.tx,
        y: cand.ty,
        selfId: u.id,
        allies: alliesLive,
        insight,
        instinct,
        personalClear01: clear ? 1 : 0,
        fleeFrom,
        approachHints,
      });
      const staffAblate =
        seek > 0
          ? 0
          : seatStaffCoverScoreBonus(
              cand.tx,
              cand.ty,
              healers,
              insight,
              clear ? 1 : 0,
            );
      const tip = seek > 0 ? seek : staffAblate;
      // Clear + min InAa first; among equals prefer survival tip, then distance.
      const score =
        (clear ? 1_000_000 : 0) -
        aaAt * 10_000 +
        tip * 100 -
        step * 10 +
        dPack * 0.5;
      if (score > bestScore) {
        bestScore = score;
        best = cand;
      }
    }
  }
  if (best) {
    if (isClear(best.tx, best.ty)) return best;
    const pushed = pushGoalClearOfPackChewRadialOnly(u, best, enemies, pad);
    if (isClear(pushed.tx, pushed.ty)) return pushed;
    return pushed;
  }
  return resolveGoalInBounds(
    u,
    u.x + baseDx * stepPx,
    u.y + baseDy * stepPx,
  );
}

/** @deprecated Use {@link seatClearOfPackChew}. */
export const seatClearOfThrallPack = seatClearOfPackChew;

/** Radial-only packChew push (no lateral fan) — pack seater fallback. */
function pushGoalClearOfPackChewRadialOnly(
  u: RaidUnit,
  goal: { tx: number; ty: number },
  enemies: readonly RaidUnit[],
  aaPad: number,
): { tx: number; ty: number } {
  const pad = Math.max(0, aaPad);
  const isClear = (x: number, y: number) =>
    clearOfPackChewAttack(x, y, u.radius, enemies, 2 + pad);
  if (isClear(goal.tx, goal.ty)) return goal;
  let tx = goal.tx;
  let ty = goal.ty;
  for (let i = 0; i < 6; i++) {
    if (isClear(tx, ty)) break;
    let nearest: RaidUnit | null = null;
    let nearestD = Infinity;
    for (const e of enemies) {
      if (!enemyIsCombatRelevant(e) || !e.combatArmed) continue;
      if (!enemyHasTag(e, RaidEnemyTag.PackChew)) continue;
      const d = Math.hypot(e.x - tx, e.y - ty);
      if (d < nearestD) {
        nearestD = d;
        nearest = e;
      }
    }
    if (!nearest) break;
    const want = enemyHitReach(nearest, u.radius) + 2 + pad;
    let dx = tx - nearest.x;
    let dy = ty - nearest.y;
    let len = Math.hypot(dx, dy);
    if (len < 1e-3) {
      dx = 0;
      dy = -1;
      len = 1;
    }
    dx /= len;
    dy /= len;
    tx = nearest.x + dx * want;
    ty = nearest.y + dy * want;
  }
  return resolveGoalInBounds(u, tx, ty);
}

/**
 * After zone-safe dodge goal: push past enemy AA by 자기보호 pad.
 * Pure charge corridors (no packChew) keep the hard skip so sidestep wins.
 * When packChew fills the keep-clear path, push packChew-only with
 * `aaClearPadPx`. Elite/charger bodies must not cancel ironhorn sidestep.
 */
function applySelfProtectAaToDodgeGoal(
  u: RaidUnit,
  goal: { tx: number; ty: number } | null,
  enemies: readonly RaidUnit[],
  zones: readonly RaidDangerZone[],
  zonePad: number,
  allies: readonly RaidUnit[] = [],
): { tx: number; ty: number } | null {
  if (goal == null || enemies.length === 0) return goal;
  // Solo leap / brass feet-in: skillZone.suppressAaGoalSteal (판결 A′ / B).
  if (suppressAaGoalStealForSkillZones(u, zones)) {
    return goal;
  }
  const livingPackChew = enemies.some(
    (e) =>
      enemyIsCombatRelevant(e) &&
      e.combatArmed &&
      enemyHasTag(e, RaidEnemyTag.PackChew),
  );
  if (chargeCorridorActive(zones) && !livingPackChew) return goal;

  // Feet in packChew AA: pack-exit seat if it also clears floors.
  if (
    livingPackChew &&
    !clearOfPackChewAttack(u.x, u.y, u.radius, enemies, 2)
  ) {
    const pack = resolvePackEscapeGoal(
      u,
      enemies,
      aaClearPadPxOf(u),
      allies,
    );
    if (
      pack &&
      pointClearsAllDangerZones(
        pack.tx,
        pack.ty,
        u.radius,
        zones,
        zonePad,
      )
    ) {
      return pack;
    }
  }

  const softAa = getSoftAaIsolateFlags();
  // Soft-safe charge_and_pack: proactive charge∩pack OR reactive feet-InAa
  // after pack-escape miss (FEAR personal clear). Not global living_pack.
  // living_pack + tournament = main Soft≪AA — ablate; regressed WT N10→0.4.
  const chargePack =
    chargeCorridorActive(zones) && livingPackChew;
  const reactiveInAa =
    livingPackChew &&
    !clearOfPackChewAttack(u.x, u.y, u.radius, enemies, 2);
  const packChewOnly =
    softAa.dualSeatGate === "living_pack"
      ? livingPackChew
      : chargePack || reactiveInAa;
  // selfProtect 0 → pad 0 (AA clear eps only).
  const aaPad = packChewOnly
    ? aaClearPadPxOf(u)
    : effectiveAaClearPadPx(u, zones);
  if (packChewOnly) {
    const pushed = pushGoalClearOfPackChewAttack(u, goal, enemies, aaPad);
    const base = enemyHitReach(
      enemies.find(
        (e) =>
          enemyIsCombatRelevant(e) &&
          e.combatArmed &&
          enemyHasTag(e, RaidEnemyTag.PackChew),
      ) ?? u,
      u.radius,
    );
    const step = base + 2 + aaPad;
    const bearings = [0.5, -0.5, 1, -1, 1.5, -1.5, 2, -2, 3, -3].map(
      (t) => t * (Math.PI / 4),
    );
    // Soft-safe first_clear ≡ main Soft≪AA min-InAa dual-seat scoring
    // (no staff pull). Early-accept first clear regresses thrall_aa|dodge.
    // Tournament branch adds insight staff filter among equals.
    {
      const cands: { tx: number; ty: number }[] = [pushed];
      for (const ang of bearings) {
        cands.push(
          resolveGoalInBounds(
            u,
            goal.tx + Math.cos(ang) * step,
            goal.ty + Math.sin(ang) * step,
          ),
        );
      }
      let best: { tx: number; ty: number } | null = null;
      let bestScore = -Infinity;
      const healers =
        allies.length > 0 ? livingHealers(allies) : ([] as RaidUnit[]);
      const feetInAa = !clearOfPackChewAttack(
        u.x,
        u.y,
        u.radius,
        enemies,
        2,
      );
      const narrow =
        softAa.narrowStaffHardFilterInAa &&
        feetInAa &&
        healers.length > 0;
      const staffClearCands = narrow
        ? cands.filter((cand) => {
            if (
              !pointClearsAllDangerZones(
                cand.tx,
                cand.ty,
                u.radius,
                zones,
                zonePad,
              )
            ) {
              return false;
            }
            if (
              !clearOfPackChewAttack(
                cand.tx,
                cand.ty,
                u.radius,
                enemies,
                2 + aaPad,
              )
            ) {
              return false;
            }
            return pointInAnyHealerStaff(cand.tx, cand.ty, healers).inside;
          })
        : [];
      const pool = staffClearCands.length > 0 ? staffClearCands : cands;
      for (const cand of pool) {
        if (
          !pointClearsAllDangerZones(
            cand.tx,
            cand.ty,
            u.radius,
            zones,
            zonePad,
          )
        ) {
          continue;
        }
        const aaAt = countPackChewInAaAt(
          cand.tx,
          cand.ty,
          u.radius,
          enemies,
          2,
        );
        const chewClear = clearOfPackChewAttack(
          cand.tx,
          cand.ty,
          u.radius,
          enemies,
          2 + aaPad,
        )
          ? 200
          : 0;
        let score = chewClear - aaAt * 100;
        if (softAa.dualSeatAlgo === "tournament" || narrow) {
          const j = judgmentOf(u);
          const seek = survivalSeekSeatBonus({
            x: cand.tx,
            y: cand.ty,
            selfId: u.id,
            allies,
            insight: j.insight,
            instinct: instinctOf(u),
            personalClear01: chewClear > 0 ? 1 : 0,
          });
          const tip =
            seek > 0
              ? seek
              : seatStaffCoverScoreBonus(
                  cand.tx,
                  cand.ty,
                  healers,
                  j.insight,
                  chewClear > 0 ? 1 : 0,
                );
          score += tip * 10;
        }
        if (score > bestScore) {
          bestScore = score;
          best = cand;
        }
      }
      return best ?? goal;
    }
  }

  const isClear = (x: number, y: number) =>
    clearOfEnemyAttack(x, y, u.radius, enemies, 2 + aaPad);
  if (isClear(goal.tx, goal.ty)) return goal;
  let tx = goal.tx;
  let ty = goal.ty;
  for (let i = 0; i < 6; i++) {
    if (isClear(tx, ty)) break;
    let nearest: RaidUnit | null = null;
    let nearestD = Infinity;
    for (const e of enemies) {
      if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
      const d = Math.hypot(e.x - tx, e.y - ty);
      if (d < nearestD) {
        nearestD = d;
        nearest = e;
      }
    }
    if (!nearest) break;
    const want = enemyHitReach(nearest, u.radius) + 2 + aaPad;
    let dx = tx - nearest.x;
    let dy = ty - nearest.y;
    let len = Math.hypot(dx, dy);
    if (len < 1e-3) {
      dx = 0;
      dy = -1;
      len = 1;
    }
    dx /= len;
    dy /= len;
    tx = nearest.x + dx * want;
    ty = nearest.y + dy * want;
  }
  const pushed = resolveGoalInBounds(u, tx, ty);
  if (
    pointClearsAllDangerZones(
      pushed.tx,
      pushed.ty,
      u.radius,
      zones,
      zonePad,
    )
  ) {
    return pushed;
  }
  return goal;
}

/**
 * Ally move gate: pad-clear the goal, then refuse straight-line transit through
 * active DangerZones (charge corridor / brass cone). On illegal transit, try
 * zone escape; if still dirty, plant feet.
 */
export function finalizeAllyMoveGoal(
  u: RaidUnit,
  tx: number,
  ty: number,
  zones: readonly RaidDangerZone[],
  mul = 1,
): { tx: number; ty: number } {
  if (zones.length === 0) return resolveGoalInBounds(u, tx, ty);
  const pad = 4;
  const steps = raidThreatSegmentSteps();
  const cleared = projectClearOfDangerZones(tx, ty, u.radius, zones, pad);
  let g = resolveGoalInBounds(u, cleared.x, cleared.y);

  const tryAccept = (cand: { tx: number; ty: number }): boolean => {
    if (
      !pointClearsAllDangerZones(cand.tx, cand.ty, u.radius, zones, pad)
    ) {
      return false;
    }
    return (
      firstEnteredDangerZoneOnSegment(
        u.x,
        u.y,
        cand.tx,
        cand.ty,
        u.radius,
        zones,
        steps,
      ) == null
    );
  };

  if (tryAccept(g)) return g;

  const entered = firstEnteredDangerZoneOnSegment(
    u.x,
    u.y,
    g.tx,
    g.ty,
    u.radius,
    zones,
    steps,
  );
  const dirtyGoal = !pointClearsAllDangerZones(
    g.tx,
    g.ty,
    u.radius,
    zones,
    pad,
  );
  const focus =
    entered ??
    (dirtyGoal
      ? activeDangerZoneAt(g.tx, g.ty, u.radius, zones)
      : null);

  if (focus) {
    let escape: { tx: number; ty: number } | null = null;
    if (focus.shape === "line") {
      escape = escapeLineZoneInBounds(u, focus, mul, zones);
    } else if (focus.shape === "cone") {
      escape = escapeConeZoneInBounds(u, focus, mul, zones);
    } else if (focus.shape === "circle") {
      const hitR = focus.range + u.radius;
      let dx = u.x - focus.x;
      let dy = u.y - focus.y;
      if (Math.hypot(dx, dy) < 10) {
        const b = raidPlayBounds();
        dx = (b.xMin + b.xMax) / 2 - focus.x;
        dy = (b.yMin + b.yMax) / 2 - focus.y;
        if (Math.hypot(dx, dy) < 1) {
          dx = 0;
          dy = -1;
        }
      }
      escape = finalizeDodgeGoal(
        u,
        escapeCircleInBounds(
          u,
          focus.x,
          focus.y,
          hitR + dodgePadPx(mul),
          dx,
          dy,
          hitR,
        ),
        zones,
        dodgePadPx(mul),
      );
    } else {
      const _exhaustive: never = focus.shape;
      void _exhaustive;
    }
    if (escape && tryAccept(escape)) return escape;

    // Retry: farther lateral / opposite side before planting on the paint.
    if (focus.shape === "line") {
      const aim = focus.aimAngle ?? 0;
      const fx = Math.cos(aim);
      const fy = Math.sin(aim);
      const lx = -fy;
      const ly = fx;
      const hw = Math.max(1, focus.halfWidth ?? focus.coneDeg ?? 20);
      const clearLat = hw + u.radius + pad;
      const lat0 = (u.x - focus.x) * lx + (u.y - focus.y) * ly;
      const preferred = lat0 >= 0 ? 1 : -1;
      for (const s of [preferred, -preferred]) {
        for (const latMul of [1.8, 2.4, 3.2]) {
          const raw = {
            tx: u.x + lx * s * clearLat * latMul,
            ty: u.y + ly * s * clearLat * latMul,
          };
          const cand = finalizeDodgeGoal(
            u,
            resolveGoalInBounds(u, raw.tx, raw.ty),
            zones,
            pad,
          );
          if (cand && tryAccept(cand)) return cand;
        }
      }
      const far = escapeLineZoneInBounds(u, focus, mul * 1.45, zones);
      if (far && tryAccept(far)) return far;
    } else if (focus.shape === "cone") {
      const far = escapeConeZoneInBounds(u, focus, mul * 1.45, zones);
      if (far && tryAccept(far)) return far;
      const aim = focus.aimAngle ?? -Math.PI / 2;
      const px = -Math.sin(aim);
      const py = Math.cos(aim);
      for (const s of [1, -1]) {
        for (const step of [48, 72, 96]) {
          const cand = finalizeDodgeGoal(
            u,
            resolveGoalInBounds(u, u.x + px * s * step, u.y + py * s * step),
            zones,
            pad,
          );
          if (cand && tryAccept(cand)) return cand;
        }
      }
    } else if (focus.shape === "circle") {
      const hitR = focus.range + u.radius;
      const clearR = hitR + dodgePadPx(mul) * 1.45;
      for (const off of [0, 0.9, -0.9, 1.8, -1.8, Math.PI]) {
        const ang = Math.atan2(u.y - focus.y, u.x - focus.x) + off;
        const cand = finalizeDodgeGoal(
          u,
          escapeCircleInBounds(
            u,
            focus.x,
            focus.y,
            clearR,
            Math.cos(ang),
            Math.sin(ang),
            hitR,
          ),
          zones,
          dodgePadPx(mul),
        );
        if (cand && tryAccept(cand)) return cand;
      }
    }

    // Multi-floor last ditch: any charge line among active zones.
    if (zones.length >= 2) {
      for (const z of zones) {
        if (z.shape !== "line" || z.skillId !== "charge") continue;
        const chargeEsc = escapeChargeApproachInBounds(u, z, mul * 1.5, zones);
        if (chargeEsc && tryAccept(chargeEsc)) return chargeEsc;
        if (
          chargeEsc &&
          pointClearsAllDangerZones(
            chargeEsc.tx,
            chargeEsc.ty,
            u.radius,
            zones,
            pad,
          )
        ) {
          return chargeEsc;
        }
      }
    }
  }

  // Hold feet only when already clear of every zone — never plant into paint.
  if (pointClearsAllDangerZones(u.x, u.y, u.radius, zones, pad)) {
    return { tx: u.x, ty: u.y };
  }
  // Feet dirty: prefer any all-clear escape over planting in the floor.
  for (const z of zones) {
    if (z.shape !== "line") continue;
    const esc = escapeLineZoneInBounds(u, z, mul * 1.55, zones);
    if (
      esc &&
      pointClearsAllDangerZones(esc.tx, esc.ty, u.radius, zones, pad)
    ) {
      return esc;
    }
  }
  for (const z of zones) {
    if (z.shape === "cone") {
      const esc = escapeConeZoneInBounds(u, z, mul * 1.55, zones);
      if (
        esc &&
        pointClearsAllDangerZones(esc.tx, esc.ty, u.radius, zones, pad)
      ) {
        return esc;
      }
    } else if (z.shape === "circle") {
      const hitR = z.range + u.radius;
      let dx = u.x - z.x;
      let dy = u.y - z.y;
      if (Math.hypot(dx, dy) < 1) {
        dx = 0;
        dy = -1;
      }
      const esc = finalizeDodgeGoal(
        u,
        escapeCircleInBounds(
          u,
          z.x,
          z.y,
          hitR + dodgePadPx(mul) * 1.55,
          dx,
          dy,
          hitR,
        ),
        zones,
        dodgePadPx(mul),
      );
      if (
        esc &&
        pointClearsAllDangerZones(esc.tx, esc.ty, u.radius, zones, pad)
      ) {
        return esc;
      }
    }
  }
  // Still dirty and no clear goal — keep moving toward best projected clear.
  const pushed = projectClearOfDangerZones(u.x, u.y, u.radius, zones, pad);
  return resolveGoalInBounds(u, pushed.x, pushed.y);
}

/**
 * Charge evade: sidestep off the **approach axis through the hero**, not
 * “step onto the painted corridor lip”. DangerZone stays hit-volume SSOT
 * (pad clear required); goal semantics use charger→land aim + hero feet.
 * Shared by red charger and yellow ironhorn (`skillId === "charge"`).
 * Candidates must clear **all** active zones (not only the feet zone).
 */
/**
 * Body-path evade for charge corridors: pad-clear lateral seats along the
 * charger→land approach axis (not paint-lip). Optional `enemies` enables
 * dual seat scoring — DZ clear + minimize predicted packChew InAa at landing
 * (Soft≪AA). Optional `allies` adds healer-staff *filter* among clear seats.
 */
export function escapeChargeApproachInBounds(
  u: RaidUnit,
  z: RaidDangerZone,
  mul: number,
  allZones: readonly RaidDangerZone[] = [z],
  danger?: RaidDanger,
  enemies: readonly RaidUnit[] = [],
  allies: readonly RaidUnit[] = [],
): { tx: number; ty: number } | null {
  if (z.shape !== "line") return null;
  const pad = dodgePadPx(mul);
  const aim = z.aimAngle ?? 0;
  const fx = Math.cos(aim);
  const fy = Math.sin(aim);
  const lx = -fy;
  const ly = fx;
  const dx = u.x - z.x;
  const dy = u.y - z.y;
  const along = dx * fx + dy * fy;
  const lat = dx * lx + dy * ly;
  const preferredSide = lat >= 0 ? 1 : -1;
  const hw = Math.max(1, z.halfWidth ?? z.coneDeg ?? 20);
  const clearLat = hw + u.radius + pad;
  // Body-path evade: past corridor clear, not paint-edge.
  // Multi-floor: enlarge lateral so one clear does not land in another zone.
  const multiFloor = allZones.length >= 2;
  const evadeLat =
    (clearLat + Math.max(pad * 0.75, u.radius * 1.25, 18)) *
    (multiFloor ? 1.35 : 1);
  const len = Math.max(1e-3, z.range);
  const alongHero = Math.max(0, Math.min(len, along));
  const b = raidPlayBounds();
  const arenaCx = (b.xMin + b.xMax) / 2;
  const arenaCy = (b.yMin + b.yMax) / 2;
  const fanAlong = ((u.id % 7) - 3) * 16;
  const fanLat = ((u.id % 5) - 2) * 10;

  type Cand = {
    tx: number;
    ty: number;
    rawTx: number;
    rawTy: number;
    latAbs: number;
  };
  const cands: Cand[] = [];
  const pushRaw = (rawTx: number, rawTy: number) => {
    const g = resolveGoalInBounds(u, rawTx, rawTy);
    const latAbs = Math.abs((g.tx - z.x) * lx + (g.ty - z.y) * ly);
    cands.push({ tx: g.tx, ty: g.ty, rawTx, rawTy, latAbs });
  };

  const latDists = multiFloor
    ? [
        evadeLat,
        evadeLat * 1.35,
        evadeLat * 1.7,
        evadeLat * 2.15,
        clearLat * 1.55,
      ]
    : [evadeLat, evadeLat * 1.35, evadeLat * 1.7, clearLat * 1.25];
  for (const latDist of latDists) {
    for (const s of [preferredSide, -preferredSide]) {
      const latStep = latDist + Math.abs(fanLat);
      // Pure lateral from feet — “get off the body path”, stay near hero along.
      pushRaw(u.x + lx * s * latStep, u.y + ly * s * latStep);
      for (const alongT of [
        alongHero + fanAlong,
        alongHero,
        alongHero - 24 + fanAlong * 0.5,
        alongHero + 24 + fanAlong * 0.5,
      ]) {
        const a = Math.max(0, Math.min(len, alongT));
        pushRaw(z.x + fx * a + lx * s * latStep, z.y + fy * a + ly * s * latStep);
      }
    }
  }

  const livingPackChew = enemies.some(
    (e) =>
      enemyIsCombatRelevant(e) &&
      e.combatArmed &&
      enemyHasTag(e, RaidEnemyTag.PackChew),
  );
  const healers = allies.length > 0 ? livingHealers(allies) : [];
  const jTraits = judgmentOf(u);
  const insight = jTraits.insight;
  const instinct = instinctOf(u);

  let best: Cand | null = null;
  let bestScore = -Infinity;
  for (const c of cands) {
    if (
      !pointClearsAllDangerZones(c.tx, c.ty, u.radius, allZones, pad)
    ) {
      continue;
    }
    const intended = dist(u.x, u.y, c.rawTx, c.rawTy);
    const actual = dist(u.x, u.y, c.tx, c.ty);
    const runway =
      intended < 8 ? 1 : Math.min(1, actual / Math.max(intended, 1e-3));
    const inward =
      -dist(c.tx, c.ty, arenaCx, arenaCy) / Math.max(b.xMax - b.xMin, 1);
    const latSign =
      (c.tx - z.x) * lx + (c.ty - z.y) * ly >= 0 ? preferredSide : -preferredSide;
    const sideBias = latSign === preferredSide ? 0.05 : 0;
    const latClear = Math.max(0, c.latAbs - clearLat);
    // Paint-lip goals (barely past pad) score poorly vs real sidesteps.
    const lipPenalty = latClear < pad * 0.5 ? -30 : 0;
    const alongGoal = (c.tx - z.x) * fx + (c.ty - z.y) * fy;
    const alongStay = -Math.abs(alongGoal - alongHero) * 0.08;
    const packAaPenalty = livingPackChew
      ? -countPackChewInAaAt(c.tx, c.ty, u.radius, enemies, 2) * 40
      : 0;
    const seek = survivalSeekSeatBonus({
      x: c.tx,
      y: c.ty,
      selfId: u.id,
      allies,
      insight,
      instinct,
      personalClear01: 1,
    });
    const staffBonus =
      seek > 0
        ? seek
        : seatStaffCoverScoreBonus(c.tx, c.ty, healers, insight, 1);
    const score =
      runway * 100 +
      Math.min(latClear, evadeLat * 1.6) * 0.6 +
      inward * 8 +
      sideBias +
      lipPenalty +
      alongStay +
      packAaPenalty +
      staffBonus;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  if (best) return { tx: best.tx, ty: best.ty };

  // Last resort: generic corridor escape (still pad-aware + all-zone clear).
  return escapeLineCorridorInBounds(u, z, mul, allZones, danger);
}

/**
 * Generic line-corridor escape (non-charge floors): pad-clear scoring among
 * ±sides / lat / along. Never early-accept bare `!inZone` (wall lip).
 * Charge uses {@link escapeChargeApproachInBounds} instead.
 * Candidates must clear **all** active zones when provided.
 */
function escapeLineCorridorInBounds(
  u: RaidUnit,
  z: RaidDangerZone,
  mul: number,
  allZones: readonly RaidDangerZone[] = [z],
  danger?: RaidDanger,
): { tx: number; ty: number } | null {
  if (z.shape !== "line") return null;
  const pad = dodgePadPx(mul);
  const aim = z.aimAngle ?? 0;
  const fx = Math.cos(aim);
  const fy = Math.sin(aim);
  const lx = -fy;
  const ly = fx;
  const dx = u.x - z.x;
  const dy = u.y - z.y;
  const along = dx * fx + dy * fy;
  const lat = dx * lx + dy * ly;
  const preferredSide = lat >= 0 ? 1 : -1;
  const hw = Math.max(1, z.halfWidth ?? z.coneDeg ?? 20);
  const clearLat = hw + u.radius + pad;
  const len = Math.max(1e-3, z.range);
  const alongClamped = Math.max(0, Math.min(len, along));
  const b = raidPlayBounds();
  const arenaCx = (b.xMin + b.xMax) / 2;
  const arenaCy = (b.yMin + b.yMax) / 2;
  const fanAlong = ((u.id % 7) - 3) * 22;
  const fanLat = ((u.id % 5) - 2) * 12;

  type Cand = { tx: number; ty: number; rawTx: number; rawTy: number };
  const cands: Cand[] = [];
  const pushRaw = (rawTx: number, rawTy: number) => {
    const g = resolveGoalInBounds(u, rawTx, rawTy);
    cands.push({ tx: g.tx, ty: g.ty, rawTx, rawTy });
  };

  for (const latMul of [1, 1.35, 1.8, 2.4]) {
    for (const s of [preferredSide, -preferredSide]) {
      for (const alongT of [
        alongClamped + fanAlong,
        Math.max(0, alongClamped - 40 + fanAlong),
        Math.min(len, alongClamped + 40 + fanAlong),
        Math.max(0, Math.min(len, alongClamped)),
        0,
        len,
      ]) {
        const rawTx =
          z.x + fx * alongT + lx * s * (clearLat * latMul + fanLat);
        const rawTy =
          z.y + fy * alongT + ly * s * (clearLat * latMul + fanLat);
        pushRaw(rawTx, rawTy);
      }
    }
  }

  const midX = z.x + fx * (len * 0.5);
  const midY = z.y + fy * (len * 0.5);
  {
    let rdx = arenaCx - midX;
    let rdy = arenaCy - midY;
    if (Math.hypot(rdx, rdy) < 1) {
      rdx = lx * preferredSide;
      rdy = ly * preferredSide;
    }
    const rn = Math.hypot(rdx, rdy) || 1;
    pushRaw(midX + (rdx / rn) * clearLat * 1.8, midY + (rdy / rn) * clearLat * 1.8);
  }

  let best: Cand | null = null;
  let bestScore = -Infinity;
  for (const c of cands) {
    if (
      !pointClearsAllDangerZones(c.tx, c.ty, u.radius, allZones, pad)
    ) {
      continue;
    }
    const intended = dist(u.x, u.y, c.rawTx, c.rawTy);
    const actual = dist(u.x, u.y, c.tx, c.ty);
    const runway =
      intended < 8 ? 1 : Math.min(1, actual / Math.max(intended, 1e-3));
    const inward =
      -dist(c.tx, c.ty, arenaCx, arenaCy) / Math.max(b.xMax - b.xMin, 1);
    const latSign =
      (c.tx - z.x) * lx + (c.ty - z.y) * ly >= 0 ? preferredSide : -preferredSide;
    const sideBias = latSign === preferredSide ? 0.05 : 0;
    const score = runway * 100 + inward * 8 + sideBias;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  if (best) return { tx: best.tx, ty: best.ty };

  let rdx = u.x - midX;
  let rdy = u.y - midY;
  if (Math.hypot(rdx, rdy) < 1) {
    rdx = lx * preferredSide;
    rdy = ly * preferredSide;
  }
  const fallback = escapeCircleInBounds(
    u,
    midX,
    midY,
    clearLat,
    rdx,
    rdy,
    hw + u.radius,
  );
  return finalizeDodgeGoal(u, fallback, allZones, pad, danger);
}

/**
 * Line DangerZone escape. Charge corridors use approach-axis sidestep;
 * other line floors use pad-clear corridor scoring.
 */
export function escapeLineZoneInBounds(
  u: RaidUnit,
  z: RaidDangerZone,
  mul: number,
  allZones: readonly RaidDangerZone[] = [z],
  danger?: RaidDanger,
  enemies: readonly RaidUnit[] = [],
  allies: readonly RaidUnit[] = [],
): { tx: number; ty: number } | null {
  if (z.shape !== "line") return null;
  if (z.skillId === "charge") {
    return escapeChargeApproachInBounds(
      u,
      z,
      mul,
      allZones,
      danger,
      enemies,
      allies,
    );
  }
  return escapeLineCorridorInBounds(u, z, mul, allZones, danger);
}

/** Cone escape: preferred side exit, then angle sweep if bounds eat clearance. */
export function escapeConeZoneInBounds(
  u: RaidUnit,
  z: RaidDangerZone,
  mul: number,
  allZones: readonly RaidDangerZone[] = [z],
  danger?: RaidDanger,
): { tx: number; ty: number } | null {
  if (z.shape !== "cone") return null;
  const pad = dodgePadPx(mul) * brassPadFactor(u, z);
  const aim = z.aimAngle ?? -Math.PI / 2;
  const half = ((z.coneDeg ?? 60) * Math.PI) / 180 / 2;
  const d0 = Math.max(40, dist(u.x, u.y, z.x, z.y));
  const ang0 = Math.atan2(u.y - z.y, u.x - z.x);
  let diff0 = ang0 - aim;
  if (!Number.isFinite(diff0)) diff0 = 0;
  while (diff0 > Math.PI) diff0 -= Math.PI * 2;
  while (diff0 < -Math.PI) diff0 += Math.PI * 2;
  const side = diff0 >= 0 ? 1 : -1;

  const tryPoint = (ang: number, ring: number): { tx: number; ty: number } | null => {
    const raw = {
      tx: z.x + Math.cos(ang) * ring,
      ty: z.y + Math.sin(ang) * ring,
    };
    const g = resolveGoalInBounds(u, raw.tx, raw.ty);
    if (
      pointClearsAllDangerZones(g.tx, g.ty, u.radius, allZones, pad)
    ) {
      return g;
    }
    return null;
  };

  const preferred = dodgeZoneGoalRaw(u, z, mul);
  {
    const g = resolveGoalInBounds(u, preferred.tx, preferred.ty);
    if (
      pointClearsAllDangerZones(g.tx, g.ty, u.radius, allZones, pad)
    ) {
      return g;
    }
  }

  const bodyPad = u.radius + pad;
  const rings = [
    d0 + Math.max(pad * 0.35, u.radius),
    d0 + pad,
    z.range + u.radius + pad,
    Math.max(d0, z.range) + pad,
  ];
  for (const ring of rings) {
    const bodyAng = Math.asin(Math.min(1, bodyPad / Math.max(ring, 1)));
    for (const extra of [0, 0.25, 0.5, 0.85, 1.2]) {
      const hit = tryPoint(aim + side * (half + bodyAng + 0.15 + extra), ring);
      if (hit) return hit;
      const other = tryPoint(aim - side * (half + bodyAng + 0.15 + extra), ring);
      if (other) return other;
    }
  }
  let dx = u.x - z.x;
  let dy = u.y - z.y;
  if (Math.hypot(dx, dy) < 1) {
    dx = Math.cos(aim + Math.PI);
    dy = Math.sin(aim + Math.PI);
  }
  const fallback = escapeCircleInBounds(
    u,
    z.x,
    z.y,
    z.range + u.radius + pad,
    dx,
    dy,
    z.range + u.radius,
  );
  return finalizeDodgeGoal(u, fallback, allZones, pad, danger);
}

/**
 * After leaving a pending DangerZone, engage/kite must not treat
 * the cleared feet as "safe to re-enter". Hold (or push to clear pad) while
 * the zone still exists and the unit is in its approach influence.
 * SoftThreat omits DangerZones — without this, windup→travel looks like the
 * attack range vanished and the archer walks into land / charge / brass.
 */
function keepClearOfPendingLeapGoal(
  u: RaidUnit,
  zones: readonly RaidDangerZone[],
  mul: number,
  danger?: RaidDanger,
  approachFrom?: { x: number; y: number } | null,
): { tx: number; ty: number } | null {
  const pad = dodgePadPx(mul);

  /** Plant at feet only when every active zone is pad-clear; else escape.
   *  Multi-floor: never plant — keep seeking all-clear (brass+leap/charge).
   *  Leap circles: require extra pad so soft-sep / one-frame lag cannot re-enter. */
  const plantOrEscapeAll = (): { tx: number; ty: number } | null => {
    const scoped = zones.filter((z) => !danger || z.danger === danger);
    const multi = scoped.length >= 2;
    const hasLeapCircle = scoped.some(
      (z) => z.shape === "circle" && z.skillId === "leap",
    );
    const leapAndCharge = zonesHaveLeapAndCharge(scoped);
    const leapPadFactor = leapAndCharge
      ? skillZoneMultiFloorPadMul("leap")
      : hasLeapCircle
        ? skillZoneSoloPadMul("leap")
        : 1;
    const brassZone = scoped.find(
      (z) => z.shape === "cone" && z.skillId === "brass",
    );
    const plantPad =
      hasLeapCircle || leapAndCharge
        ? pad * leapPadFactor
        : brassZone
          ? pad * brassPadFactor(u, brassZone)
          : pad;
    const chargeApproachMul = leapAndCharge
      ? mul * skillZoneMultiFloorApproachMul("charge")
      : mul;
    if (
      !multi &&
      pointClearsAllDangerZones(u.x, u.y, u.radius, zones, plantPad)
    ) {
      return finalizeDodgeGoal(u, { tx: u.x, ty: u.y }, zones, pad, danger);
    }
    if (
      !multi &&
      hasLeapCircle &&
      pointClearsAllDangerZones(u.x, u.y, u.radius, zones, pad)
    ) {
      // Lip-clear of leap: step laterally off the pad — do not plant on the
      // hit ring, and do not push further south along radial-from-feet.
      const leap = scoped.find(
        (z) => z.shape === "circle" && z.skillId === "leap",
      );
      if (leap) {
        const solo = zonesHaveSoloLeap(scoped);
        const { dx, dy } = solo
          ? soloLeapEscapeDir(u, leap, approachFrom)
          : circleDangerEscapeDir(u, leap, approachFrom);
        const hitR = leap.range + u.radius;
        return finalizeDodgeGoal(
          u,
          escapeCircleInBounds(
            u,
            leap.x,
            leap.y,
            hitR + plantPad,
            dx,
            dy,
            hitR,
          ),
          zones,
          plantPad,
          danger,
        );
      }
    }
    if (
      multi &&
      pointClearsAllDangerZones(u.x, u.y, u.radius, zones, pad)
    ) {
      // Already clear of all paints — still step away from nearest threat so
      // overlapping resolve windows cannot catch stationary feet.
      const nearest = scoped.reduce<(typeof scoped)[0] | null>((best, z) => {
        const d = Math.hypot(u.x - z.x, u.y - z.y);
        if (!best) return z;
        const bd = Math.hypot(u.x - best.x, u.y - best.y);
        return d < bd ? z : best;
      }, null);
      const stepMul = leapAndCharge
        ? skillZoneMultiFloorPadMul("leap")
        : zonesHaveSoloLeap(scoped)
          ? skillZoneSoloPadMul("leap")
          : 1.25;
      if (nearest?.shape === "line") {
        const esc = escapeLineZoneInBounds(
          u,
          nearest,
          leapAndCharge ? chargeApproachMul : mul * 1.2,
          zones,
          danger,
        );
        if (esc) return esc;
      } else if (nearest?.shape === "cone") {
        const esc = escapeConeZoneInBounds(u, nearest, mul * 1.2, zones, danger);
        if (esc) return esc;
      } else if (nearest?.shape === "circle") {
        const hitR = nearest.range + u.radius;
        const solo =
          zonesHaveSoloLeap(scoped) && nearest.skillId === "leap";
        const { dx, dy } = solo
          ? soloLeapEscapeDir(u, nearest, approachFrom)
          : circleDangerEscapeDir(u, nearest, approachFrom);
        return finalizeDodgeGoal(
          u,
          escapeCircleInBounds(
            u,
            nearest.x,
            nearest.y,
            hitR + pad * stepMul,
            dx,
            dy,
            hitR,
          ),
          zones,
          leapAndCharge ? plantPad : pad,
          danger,
        );
      }
    }
    // Feet still hit another floor — charge-priority then any line/cone/circle.
    const charge = zones.find(
      (z) =>
        z.shape === "line" &&
        z.skillId === "charge" &&
        (!danger || z.danger === danger),
    );
    if (charge) {
      const esc = escapeChargeApproachInBounds(
        u,
        charge,
        chargeApproachMul,
        zones,
        danger,
      );
      if (esc) return esc;
    }
    for (const z of zones) {
      if (danger && z.danger !== danger) continue;
      if (z.shape === "line") {
        const esc = escapeLineZoneInBounds(u, z, mul, zones, danger);
        if (esc) return esc;
      } else if (z.shape === "cone") {
        const esc = escapeConeZoneInBounds(u, z, mul, zones, danger);
        if (esc) return esc;
      } else if (z.shape === "circle") {
        const hitR = z.range + u.radius;
        const { dx, dy } = circleDangerEscapeDir(u, z, approachFrom);
        const clearPad = leapAndCharge && z.skillId === "leap" ? plantPad : pad;
        const esc = finalizeDodgeGoal(
          u,
          escapeCircleInBounds(
            u,
            z.x,
            z.y,
            hitR + clearPad,
            dx,
            dy,
            hitR,
          ),
          zones,
          clearPad,
          danger,
        );
        if (esc) return esc;
      } else {
        const _exhaustive: never = z.shape;
        void _exhaustive;
      }
    }
    return null;
  };

  let nearest: RaidDangerZone | null = null;
  let nearestD = Infinity;
  for (const z of zones) {
    if (z.skillId !== "leap" && z.skillId !== "charge") continue;
    if (danger && z.danger !== danger) continue;
    if (z.shape !== "circle") continue;
    const d = Math.hypot(u.x - z.x, u.y - z.y);
    if (d < nearestD) {
      nearestD = d;
      nearest = z;
    }
  }
  if (nearest) {
    const hitR = nearest.range + u.radius;
    const clearR = hitR + pad;
    // Influence: clear pad + short approach so path-to-boss through land sticks.
    const influenceR = clearR + Math.max(48, u.attackRange * 0.4);
    if (nearestD <= influenceR) {
      if (nearestD < clearR) {
        const { dx, dy } = circleDangerEscapeDir(u, nearest, approachFrom);
        return finalizeDodgeGoal(
          u,
          escapeCircleInBounds(
            u,
            nearest.x,
            nearest.y,
            clearR,
            dx,
            dy,
            hitR,
          ),
          zones,
          pad,
          danger,
        );
      }
      // Outside this land radius+pad — plant only if all zones clear.
      return plantOrEscapeAll();
    }
  }

  // Sticky keep-clear for charge (and other) line corridors — wide approach
  // envelope so backline archers sidestep before walking into the paint.
  let nearestLine: RaidDangerZone | null = null;
  let nearestLat = Infinity;
  for (const z of zones) {
    if (z.shape !== "line") continue;
    if (danger && z.danger !== danger) continue;
    const aim = z.aimAngle ?? 0;
    const fx = Math.cos(aim);
    const fy = Math.sin(aim);
    const lx = -fy;
    const ly = fx;
    const dx = u.x - z.x;
    const dy = u.y - z.y;
    const along = dx * fx + dy * fy;
    const len = Math.max(1e-3, z.range);
    const hw = Math.max(1, z.halfWidth ?? z.coneDeg ?? 20);
    const clearLat = hw + u.radius + pad;
    const influenceAlong = Math.max(48, pad, u.attackRange * 0.35);
    if (along < -influenceAlong || along > len + influenceAlong) continue;
    const lat = dx * lx + dy * ly;
    const latAbs = Math.abs(lat);
    const influenceLat = clearLat + Math.max(u.attackRange * 0.5, 96);
    if (latAbs > influenceLat) continue;
    if (latAbs < nearestLat) {
      nearestLat = latAbs;
      nearestLine = z;
    }
  }
  if (nearestLine) {
    const hw = Math.max(1, nearestLine.halfWidth ?? nearestLine.coneDeg ?? 20);
    const clearLat = hw + u.radius + pad;
    if (nearestLat < clearLat) {
      return escapeLineZoneInBounds(u, nearestLine, mul, zones, danger);
    }
    return plantOrEscapeAll();
  }

  // Brass / cone telegraphs: sticky approach envelope (same contract as leap).
  // Mid-HP backline (판결 B): stronger pad clearance via brassPadFactor only —
  // do not widen approachAng (Soft re-entry churn).
  let nearestCone: RaidDangerZone | null = null;
  let nearestConeD = Infinity;
  for (const z of zones) {
    if (z.shape !== "cone") continue;
    if (danger && z.danger !== danger) continue;
    const conePad = pad * brassPadFactor(u, z);
    const d = Math.hypot(u.x - z.x, u.y - z.y);
    const clearR = z.range + u.radius + conePad;
    const influenceR = clearR + Math.max(48, u.attackRange * 0.4);
    if (d > influenceR) continue;
    const aim = z.aimAngle ?? -Math.PI / 2;
    const half = ((z.coneDeg ?? 60) * Math.PI) / 180 / 2;
    const ang = Math.atan2(u.y - z.y, u.x - z.x);
    let diff = ang - aim;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const approachAng = half + 0.4;
    if (d > u.radius + 40 && Math.abs(diff) > approachAng) continue;
    if (d < nearestConeD) {
      nearestConeD = d;
      nearestCone = z;
    }
  }
  if (!nearestCone) return null;

  const nearestConePad = pad * brassPadFactor(u, nearestCone);
  if (
    !pointClearsDangerZone(u.x, u.y, u.radius, nearestCone, nearestConePad)
  ) {
    return escapeConeZoneInBounds(u, nearestCone, mul, zones, danger);
  }
  return plantOrEscapeAll();
}

/**
 * Bounds-aware dodge goal from DangerZones only.
 * Optional `danger` filter scopes *which feet zones open escape geometry*;
 * callers that Role-gate (tank red-only) should pass `undefined` here so goals
 * still pad-clear **all** active floors once dodge is open (SSOT).
 * Leap/charge land stays sticky after clear: missing windup telegraph ≠ safe to re-enter.
 */
export function dodgeGoalInBounds(
  u: RaidUnit,
  boss: RaidBoss,
  mul: number,
  extras: readonly RaidDangerCaster[] = [],
  danger?: RaidDanger,
  includeBoss = true,
  allies: readonly RaidUnit[] = [],
): { tx: number; ty: number } | null {
  const distPx = dodgePadPx(mul);
  const zones = zonesWithExtras(boss, extras, includeBoss);
  const enemies = (extras ?? []).filter(
    (e): e is RaidUnit => typeof (e as RaidUnit).team === "number",
  ) as RaidUnit[];
  // AA push on dodge wrap (selfProtect).
  const wrap = (goal: { tx: number; ty: number } | null, zonePad = distPx) =>
    applySelfProtectAaToDodgeGoal(u, goal, enemies, zones, zonePad, allies);

  if (!unitInDangerZones(u, zones, danger)) {
    return wrap(
      keepClearOfPendingLeapGoal(u, zones, mul, danger, {
        x: boss.x,
        y: boss.y,
      }),
    );
  }

  const inFeet = zones.filter(
    (z) =>
      (!danger || z.danger === danger) &&
      pointInDangerZone(u.x, u.y, u.radius, z),
  );

  // Multi-floor: charge-priority, then leap land disks, then cones; all-zones clear.
  // leap∩charge (Colossus mid/late): stronger charge sidestep + leap pad.
  // solo leap (+brass etc): skillZone.leap.soloPadMul / radial-out (판결 A 잔여).
  if (inFeet.length >= 2) {
    const leapAndCharge = zonesHaveLeapAndCharge(zones);
    const soloLeap = zonesHaveSoloLeap(zones);
    const multiMul = leapAndCharge
      ? mul * skillZoneMultiFloorApproachMul("charge")
      : mul * 1.25;
    const multiPad = leapAndCharge
      ? dodgePadPx(mul) * skillZoneMultiFloorPadMul("leap")
      : soloLeap
        ? dodgePadPx(multiMul) * skillZoneSoloPadMul("leap")
        : dodgePadPx(multiMul);
    const charge = inFeet.find(
      (z) => z.shape === "line" && z.skillId === "charge",
    );
    if (charge) {
      const goal = escapeChargeApproachInBounds(
        u,
        charge,
        multiMul,
        zones,
        danger,
        enemies,
        allies,
      );
      if (
        goal &&
        pointClearsAllDangerZones(
          goal.tx,
          goal.ty,
          u.radius,
          zones,
          multiPad,
          danger,
        )
      ) {
        return wrap(goal, multiPad);
      }
      if (goal) return wrap(goal, multiPad);
    }
    const tryCircle = (z: RaidDangerZone) => {
      const hitR = z.range + u.radius;
      const approach = { x: boss.x, y: boss.y };
      const { dx, dy } =
        soloLeap && z.skillId === "leap"
          ? soloLeapEscapeDir(u, z, approach)
          : circleDangerEscapeDir(u, z, approach);
      return finalizeDodgeGoal(
        u,
        escapeCircleInBounds(
          u,
          z.x,
          z.y,
          hitR + multiPad,
          dx,
          dy,
          hitR,
        ),
        zones,
        multiPad,
        danger,
      );
    };
    // Leap land before brass cone — land resolve is the lethal short window.
    // When leap∩charge, prefer leap disks after charge attempt above.
    for (const z of inFeet) {
      if (z.shape === "circle") {
        const goal = tryCircle(z);
        if (
          goal &&
          pointClearsAllDangerZones(
            goal.tx,
            goal.ty,
            u.radius,
            zones,
            multiPad,
            danger,
          )
        ) {
          return wrap(goal, multiPad);
        }
      }
    }
    for (const z of inFeet) {
      if (z.shape === "cone") {
        const goal = escapeConeZoneInBounds(u, z, multiMul, zones, danger);
        if (goal) return wrap(goal, multiPad);
      } else if (z.shape === "line") {
        const goal = escapeLineZoneInBounds(u, z, multiMul, zones, danger);
        if (goal) return wrap(goal, multiPad);
      } else if (z.shape === "circle") {
        const goal = tryCircle(z);
        if (goal) return wrap(goal, multiPad);
      } else {
        const _exhaustive: never = z.shape;
        void _exhaustive;
      }
    }
  }

  const active = activeDangerZoneAt(u.x, u.y, u.radius, zones, danger);
  if (!active) {
    const goal = keepClearOfPendingLeapGoal(u, zones, mul, danger, {
      x: boss.x,
      y: boss.y,
    });
    if (goal == null) {
      raidFreezeDiag.noteDodgeGoalNull(
        u.id,
        raidFreezeDiag.snapZones(
          zones.filter((z) => !danger || z.danger === danger),
        ),
        {
          reason: "no_active_zone",
          dangerFilter: danger ?? "any",
          x: u.x,
          y: u.y,
        },
      );
    }
    return wrap(goal);
  }

  if (active.shape === "circle") {
    const hitR = active.range + u.radius;
    const soloLeap =
      active.skillId === "leap" && zonesHaveSoloLeap(zones);
    const circlePad = soloLeap
      ? distPx * skillZoneSoloPadMul("leap")
      : distPx;
    const approach = { x: boss.x, y: boss.y };
    const { dx, dy } = soloLeap
      ? soloLeapEscapeDir(u, active, approach)
      : circleDangerEscapeDir(u, active, approach);
    return wrap(
      finalizeDodgeGoal(
        u,
        escapeCircleInBounds(
          u,
          active.x,
          active.y,
          hitR + circlePad,
          dx,
          dy,
          hitR,
        ),
        zones,
        circlePad,
        danger,
      ),
      circlePad,
    );
  }

  if (active.shape === "line") {
    const goal = escapeLineZoneInBounds(
      u,
      active,
      mul,
      zones,
      danger,
      enemies,
      allies,
    );
    if (goal == null) {
      raidFreezeDiag.noteDodgeGoalNull(
        u.id,
        raidFreezeDiag.snapZones([active]),
        { reason: "escape_line_null", dangerFilter: danger ?? "any" },
      );
    }
    return wrap(goal);
  }

  const goal = escapeConeZoneInBounds(u, active, mul, zones, danger);
  if (goal == null) {
    raidFreezeDiag.noteDodgeGoalNull(
      u.id,
      raidFreezeDiag.snapZones([active]),
      { reason: "escape_cone_null", dangerFilter: danger ?? "any" },
    );
  }
  return wrap(goal);
}

export function dodgeIntent(
  u: RaidUnit,
  dodge: { tx: number; ty: number },
  boss: RaidBoss,
  /** Kite-shoot while clearing — only add targets (boss telegraphs stay silent). */
  fire?: Pick<AiIntent, "wantAttack" | "faceX" | "faceY" | "attackTargetId">,
): AiIntent {
  // dodgeDistMul / redDodgeMul apply to goal distance/pad only — never intent.speedMul
  // (run-speed boost during dodge is a cheat; user veto).
  const shootAdd =
    fire != null && fire.attackTargetId != null && fire.wantAttack;
  return {
    tx: dodge.tx,
    ty: dodge.ty,
    wantAttack: shootAdd ? true : false,
    wantHealId: null,
    wantInterrupt: false,
    ...(shootAdd
      ? {
          faceX: fire.faceX,
          faceY: fire.faceY,
          attackTargetId: fire.attackTargetId,
        }
      : { ...faceBoss(boss), attackTargetId: null }),
  };
}

// ── perception / survival ──────────────────────────────────────────────────

/** Ally anchor for cooperative retreats: HP-weighted centroid of nearby
 *  living allies (self excluded) → front tank → arena center. */
export function allyAnchor(
  u: RaidUnit,
  allies: readonly RaidUnit[],
): { x: number; y: number } {
  let wx = 0;
  let wy = 0;
  let w = 0;
  for (const a of allies) {
    if (a.id === u.id || a.hp <= 0) continue;
    if (dist(u.x, u.y, a.x, a.y) > 260) continue;
    const weight = 0.4 + 0.6 * (a.hp / a.maxHp);
    wx += a.x * weight;
    wy += a.y * weight;
    w += weight;
  }
  if (w > 0) return { x: wx / w, y: wy / w };
  const tank = frontTank(allies);
  if (tank) return { x: tank.x, y: tank.y };
  const b = raidPlayBounds();
  return { x: (b.xMin + b.xMax) / 2, y: (b.yMin + b.yMax) / 2 };
}

/**
 * Soft-pull a goal off playBounds lips toward an ally/formation anchor.
 * Shared by survive retreat, spaceOutBossGoal, and kit post.
 */
export function pullGoalOffLip(
  u: RaidUnit,
  goal: { tx: number; ty: number },
  anchor: { x: number; y: number },
  lipPx = 32,
): { tx: number; ty: number } {
  const bounds = raidPlayBounds();
  const edgeProx = Math.min(
    goal.tx - bounds.xMin - u.radius,
    bounds.xMax - u.radius - goal.tx,
    goal.ty - bounds.yMin - u.radius,
    bounds.yMax - u.radius - goal.ty,
  );
  if (edgeProx >= lipPx) return goal;
  const pull = (lipPx - Math.max(0, edgeProx)) / lipPx;
  const t = 0.55 + 0.35 * pull;
  return resolveGoalInBounds(
    u,
    goal.tx + (anchor.x - goal.tx) * t,
    goal.ty + (anchor.y - goal.ty) * (t * 0.55),
  );
}

/**
 * Survival pressure 0..1: low HP (quadratic) + recent damage + threat
 * proximity. T2 rules gate on this — being hurt is never ignorable.
 */
export function dangerScore(
  u: RaidUnit,
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
  time: number,
): number {
  const hpPct = u.maxHp > 0 ? u.hp / u.maxHp : 1;
  const pressure = underPressure(u.hit, time, 1.6) ? 1 : 0;
  let proximity = 0;
  for (const e of enemies) {
    if (e.hp <= 0) continue;
    const d = dist(u.x, u.y, e.x, e.y);
    const r = (e.attackRange + u.radius) * 1.6;
    proximity = Math.max(proximity, Math.max(0, 1 - d / Math.max(1, r)));
  }
  const bd = dist(u.x, u.y, boss.x, boss.y);
  const br = (boss.attackRange + boss.radius) * 1.3;
  proximity = Math.max(proximity, Math.max(0, 1 - bd / Math.max(1, br)));
  const lowHp = (1 - hpPct) * (1 - hpPct);
  return Math.min(1, 0.55 * lowHp + 0.25 * pressure + 0.2 * proximity);
}

/**
 * Retreat threat source — aggro language, no forced boss fallback.
 * 1) living lastAttacker  2) topThreat on unit.aggro among hostiles+boss
 * 3) null → caller uses ally anchor only
 */
export function retreatThreatPoint(
  u: RaidUnit,
  boss: RaidBoss,
  enemies: readonly RaidUnit[],
  time: number,
): { x: number; y: number } | null {
  const id = u.hit.lastAttackerId;
  if (id >= 0) {
    const e = enemies.find((x) => x.id === id && x.hp > 0);
    if (e) return { x: e.x, y: e.y };
  }

  type Cand = { id: number; x: number; y: number };
  const cands: Cand[] = [{ id: RAID_AGGRO_BOSS_ID, x: boss.x, y: boss.y }];
  for (const e of enemies) {
    if (e.hp <= 0) continue;
    cands.push({ id: e.id, x: e.x, y: e.y });
  }
  const currentId =
    u.brain.targetId >= 0 ? u.brain.targetId : RAID_AGGRO_BOSS_ID;
  const pick = topThreatTarget(u.aggro, cands, {
    idOf: (c) => c.id,
    currentId,
    sticky: raidAggroSticky(),
    time,
  });
  return pick ? { x: pick.x, y: pick.y } : null;
}

const RETREAT_SCRATCH = { x: 0, y: 0 };

/** Predicted packChew bodies whose hit-reach covers (x,y). */
function countPackChewInAaAt(
  x: number,
  y: number,
  radius: number,
  enemies: readonly RaidUnit[],
  eps = 2,
): number {
  let n = 0;
  for (const e of enemies) {
    if (
      !enemyIsCombatRelevant(e) ||
      !e.combatArmed ||
      !enemyHasTag(e, RaidEnemyTag.PackChew)
    ) {
      continue;
    }
    if (Math.hypot(e.x - x, e.y - y) <= enemyHitReach(e, radius) + eps) {
      n += 1;
    }
  }
  return n;
}

function countPackChewInAa(
  u: Pick<RaidUnit, "x" | "y" | "radius">,
  enemies: readonly RaidUnit[],
  eps = 2,
): number {
  return countPackChewInAaAt(u.x, u.y, u.radius, enemies, eps);
}

/**
 * Pack-exit seat while feet in packChew AA. Step lengthens when packChewInAa
 * rises vs last tick (re-attach). Optional allies → insight joint-seat bias
 * (stay coverable by healer staff without Claim B chase).
 */
export function resolvePackEscapeGoal(
  u: RaidUnit,
  enemies: readonly RaidUnit[],
  aaPad: number,
  allies?: readonly RaidUnit[],
): { tx: number; ty: number } | null {
  const pad = Math.max(0, aaPad);
  const aaNow = countPackChewInAa(u, enemies, 2);
  if (aaNow <= 0) {
    u.brain.thrallEscapeAa = 0;
    u.brain.thrallEscapeSeat = null;
    return null;
  }
  // Option A 직감: ≤20 → no personal seat-break (SSOT: 0이면 안 깨고 버팀).
  // Soft≪AA tip (`survivalSeekSeatBonus`) also 0. Soft stack ×1.22 ≠ 직감.
  // Default Soft party instinct 58–78 still uses pack-escape floor.
  if (!instinctAllowsSeatBreak(instinctOf(u))) {
    u.brain.thrallEscapeAa = aaNow;
    u.brain.thrallEscapeSeat = null;
    return null;
  }
  const flee = packChewFleePoint(u, enemies);
  if (!flee) {
    u.brain.thrallEscapeAa = 0;
    u.brain.thrallEscapeSeat = null;
    return null;
  }
  const rose = aaNow > u.brain.thrallEscapeAa;
  u.brain.thrallEscapeAa = aaNow;
  // Soft≪AA: multi-thrall chew (aa≥2) needs longer exit steps — stereotype
  // thrall_aa|*|survive|thrallAA>0. Keep mild (≤2.2×); heavier push / pad28
  // floor regressed RaidRng Soft (0.3→0.4 / 0.65). SoftStackProbe dials for Claim S.
  const multi = aaNow >= 2;
  const multiRose = softStackMultiRoseMul();
  const step = rose
    ? RETREAT_STEP_PX * (multi ? multiRose : 1.6)
    : RETREAT_STEP_PX * (multi ? 1.5 : 1);
  let g = seatClearOfPackChew(u, flee, enemies, pad, step, allies);
  if (allies && allies.length > 0) {
    const healers = livingHealers(allies);
    if (healers.length > 0) {
      const j = judgmentOf(u);
      const biased = biasSeatTowardHealerCover(g, healers, j.insight);
      if (biased.pulled) {
        const cand = resolveGoalInBounds(u, biased.tx, biased.ty);
        if (clearOfPackChewAttack(cand.tx, cand.ty, u.radius, enemies, 2 + pad)) {
          g = cand;
        }
      }
    }
  }
  u.brain.thrallEscapeSeat = { x: g.tx, y: g.ty };
  return g;
}

/** @deprecated Use {@link resolvePackEscapeGoal}. */
export const resolveThrallEscapeGoal = resolvePackEscapeGoal;

/**
 * While feet are in packChew AA, flee the chewing pack (centroid of in-AA
 * packChew, else nearest) — not lastAttacker/boss sticky.
 */
export function packChewFleePoint(
  u: Pick<RaidUnit, "x" | "y" | "radius">,
  enemies: readonly RaidUnit[],
): { x: number; y: number; nearestId: number } | null {
  let sx = 0;
  let sy = 0;
  let n = 0;
  let nearest: RaidUnit | null = null;
  let nearestD = Infinity;
  let nearestInAa: RaidUnit | null = null;
  let nearestInAaD = Infinity;
  for (const e of enemies) {
    if (
      !enemyIsCombatRelevant(e) ||
      !e.combatArmed ||
      !enemyHasTag(e, RaidEnemyTag.PackChew)
    ) {
      continue;
    }
    const d = Math.hypot(e.x - u.x, e.y - u.y);
    if (d < nearestD) {
      nearestD = d;
      nearest = e;
    }
    if (d <= enemyHitReach(e, u.radius) + 2) {
      sx += e.x;
      sy += e.y;
      n += 1;
      if (d < nearestInAaD) {
        nearestInAaD = d;
        nearestInAa = e;
      }
    }
  }
  if (n > 0 && nearestInAa) {
    return {
      x: sx / n,
      y: sy / n,
      nearestId: nearestInAa.id,
    };
  }
  if (!nearest) return null;
  return { x: nearest.x, y: nearest.y, nearestId: nearest.id };
}

/** @deprecated Use {@link packChewFleePoint}. */
export const thrallChewFleePoint = packChewFleePoint;

/**
 * Cooperative retreat: away from aggro threat source, continuously biased
 * toward ally by adjutantFollowMul (0 = pure flee, max = strong ally pull).
 * SoftThreat hotter guard only — no multi-candidate score tournament.
 */
export function retreatToAllyIntent(
  u: RaidUnit,
  boss: RaidBoss,
  allies: readonly RaidUnit[],
  enemies: readonly RaidUnit[],
  focusBoss: boolean,
  time: number,
  cohesion = 0.8,
  includeBoss = true,
): AiIntent {
  const b = raidPlayBounds();
  const boundsCenter = {
    x: (b.xMin + b.xMax) / 2,
    y: (b.yMin + b.yMax) / 2,
  };
  const hasAlly = allies.some((a) => a.id !== u.id && a.hp > 0);
  const coh = Math.max(0, cohesion);
  const anchor = hasAlly ? allyAnchor(u, allies) : boundsCenter;
  const allyBias = hasAlly ? coh : 0;
  const vols = buildLocalHostileVolumes(boss, time, u.radius, {
    enemies,
    includeBoss,
  });
  const hereThreat = sampleThreatAt(u.x, u.y, vols);
  // Soft≪AA: while thralls chew feet, drop ally pull and pack-exit.
  // Soft→ally-anchor forbidden (walks back into chew). No far-XY sticky.
  const aaPad = aaClearPadPxOf(u);
  const escapeGoal = resolvePackEscapeGoal(u, enemies, aaPad, allies);
  const thrallChew = escapeGoal != null;
  const chewFlee = thrallChew ? packChewFleePoint(u, enemies) : null;
  const threat =
    chewFlee ?? retreatThreatPoint(u, boss, enemies, time);
  const stepBias = thrallChew ? 0 : allyBias;

  let g: { tx: number; ty: number };
  if (escapeGoal) {
    g = escapeGoal;
    if (chewFlee) {
      const antiPack = {
        x: u.x + (u.x - chewFlee.x),
        y: u.y + (u.y - chewFlee.y),
      };
      g = pullGoalOffLip(u, g, antiPack);
      if (!clearOfPackChewAttack(g.tx, g.ty, u.radius, enemies, 2 + aaPad)) {
        g = escapeGoal;
      }
    }
  } else if (!threat) {
    g = resolveGoalInBounds(u, anchor.x, anchor.y);
    g = pullGoalOffLip(u, g, anchor);
    g = pushGoalClearOfPackChewAttack(u, g, enemies, aaPad);
  } else {
    const bounds = insetRect(raidPlayBounds(), u.radius);
    const stepGoal = (bias: number): { tx: number; ty: number } => {
      retreatDirBiased(
        u.x,
        u.y,
        threat.x,
        threat.y,
        anchor.x,
        anchor.y,
        bias,
        RETREAT_SCRATCH,
      );
      let dir = { x: RETREAT_SCRATCH.x, y: RETREAT_SCRATCH.y };
      if (retreatBlockedAtEdge(u.x, u.y, dir.x, dir.y, bounds, 16, 0.15)) {
        retreatDirBiased(
          u.x,
          u.y,
          threat.x,
          threat.y,
          anchor.x,
          anchor.y,
          bias,
          RETREAT_SCRATCH,
        );
        dir = { x: RETREAT_SCRATCH.x, y: RETREAT_SCRATCH.y };
      }
      return resolveGoalInBounds(
        u,
        u.x + dir.x * RETREAT_STEP_PX,
        u.y + dir.y * RETREAT_STEP_PX,
      );
    };
    const cand = stepGoal(stepBias);
    const t = sampleThreatAt(cand.tx, cand.ty, vols);
    // Hotter Soft volume → retry with pure flee (bias 0).
    g =
      t > hereThreat + 1 && stepBias > 0 ? stepGoal(0) : cand;
    if (sampleThreatAt(g.tx, g.ty, vols) > hereThreat + 1) {
      // Prefer ally/formation anchor over planting on a wall lip.
      g = resolveGoalInBounds(u, anchor.x, anchor.y);
    }
    g = pullGoalOffLip(u, g, anchor);
    g = pushGoalClearOfPackChewAttack(u, g, enemies, aaPad);
  }

  // Soft≪AA: charge corridor is Soft foresight (≠ packChew AA). Survive
  // retreat that ignores painted charge approach → charge|dps|survive deaths.
  // Always-on painted Soft-clear Soft-regressed N20 0.2→0.35 — keep
  // feet/goal-in OR near-corridor approach (thrallAA=0 seed12).
  {
    const zones = collectRaidDangerZones(boss, { enemies, includeBoss });
    if (chargeCorridorActive(zones)) {
      const charge = zones.find(
        (z) => z.shape === "line" && z.skillId === "charge",
      );
      if (charge && charge.shape === "line") {
        const feetInCharge = pointInDangerZone(
          u.x,
          u.y,
          u.radius,
          charge,
        );
        const goalInCharge = !pointClearsAllDangerZones(
          g.tx,
          g.ty,
          u.radius,
          zones,
          0,
        );
        const aim = charge.aimAngle ?? 0;
        const fx = Math.cos(aim);
        const fy = Math.sin(aim);
        const lx = -fy;
        const ly = fx;
        const dx = u.x - charge.x;
        const dy = u.y - charge.y;
        const along = dx * fx + dy * fy;
        const lat = Math.abs(dx * lx + dy * ly);
        const hw = Math.max(1, charge.halfWidth ?? charge.coneDeg ?? 20);
        const clearLat = hw + u.radius + 12;
        const len = Math.max(1e-3, charge.range);
        // EncounterKnowledge pack∩charge: widen Soft foresight band (통찰).
        const nearMul = encounterPackChargeForesightActive(
          boss,
          enemies,
          includeBoss,
        )
          ? 3.5
          : 2.4;
        const nearApproach =
          !thrallChew &&
          lat < clearLat * nearMul &&
          along > -28 &&
          along < len + 48;
        if (feetInCharge || goalInCharge || nearApproach) {
          const esc = escapeChargeApproachInBounds(
            u,
            charge,
            1,
            zones,
            undefined,
            enemies,
            allies,
          );
          if (
            esc &&
            pointClearsAllDangerZones(esc.tx, esc.ty, u.radius, zones, 0)
          ) {
            g = esc;
          }
        }
      }
    }
  }

  const inReach = pickInWeaponReachAdd(u, enemies);
  // Soft≪AA: brief escape burst while feet chewed — mobility only, not combat
  // coeffs / pad floor. Multi 1.35 Soft-regressed N20 0.65 — keep mild 1.22.
  // SoftStackProbe: ship only applies 1.22 (Claim S ladder).
  const surviveMul = softStackSurviveSpeedMul();
  const escapeBurst =
    thrallChew && surviveMul !== 1 ? { speedMul: surviveMul } : {};
  if (inReach) {
    // Restore sticky so dodge→survive does not leave wantTarget on a dead add.
    u.brain.targetId = inReach.id;
    return {
      tx: g.tx,
      ty: g.ty,
      wantAttack: true,
      wantHealId: null,
      wantInterrupt: false,
      faceX: inReach.x,
      faceY: inReach.y,
      attackTargetId: inReach.id,
      ...escapeBurst,
    };
  }
  if (thrallChew && chewFlee) {
    const chew = enemies.find(
      (e) => e.id === chewFlee.nearestId && e.hp > 0,
    );
    if (chew) {
      u.brain.targetId = chew.id;
      const chewD = dist(u.x, u.y, chew.x, chew.y);
      const canShoot = chewD < addAttackReach(u, chew.radius);
      return {
        tx: g.tx,
        ty: g.ty,
        wantAttack: canShoot,
        wantHealId: null,
        wantInterrupt: false,
        faceX: chew.x,
        faceY: chew.y,
        attackTargetId: canShoot ? chew.id : null,
        ...escapeBurst,
      };
    }
  }
  const hostile = pickEngageTarget(
    u,
    boss,
    enemies,
    u.classId,
    focusBoss,
    time,
    includeBoss,
    allies,
  );
  const hostileD = dist(u.x, u.y, hostile.x, hostile.y);
  const hostileReach = hostile.isBoss
    ? hostileAttackReach(u, boss)
    : addAttackReach(u, resolveAddRadius(enemies, hostile.id));
  const canShoot = hostile.isBoss
    ? hostileD <= hostileReach
    : hostileD < hostileReach;
  return {
    tx: g.tx,
    ty: g.ty,
    wantAttack: canShoot,
    wantHealId: null,
    wantInterrupt: false,
    ...faceTarget(hostile),
    ...escapeBurst,
  };
}

/**
 * DPS space / low-HP kite: keep a boss-min ring, but pick a bearing that
 * stays away from the boss while preferring the ally (or gesture formation)
 * anchor and avoiding playBounds lips (survive-class). When `onlyWhenCloser`,
 * returns null if already outside `minDist` (space gate).
 */
export function spaceOutBossGoal(
  u: RaidUnit,
  boss: RaidBoss,
  allies: readonly RaidUnit[],
  minDist: number,
  opts: {
    onlyWhenCloser?: boolean;
    time?: number;
  } = {},
): { tx: number; ty: number } | null {
  const onlyWhenCloser = opts.onlyWhenCloser !== false;
  const d = dist(u.x, u.y, boss.x, boss.y);
  if (onlyWhenCloser && d >= minDist) return null;

  const standRing = boss.radius + u.attackRange * 0.8;
  const reach = hostileAttackReach(u, boss);
  const maxRing = reach * 0.92;
  const out = Math.min(Math.max(minDist, standRing), maxRing);
  const ally = allyAnchor(u, allies);
  const ref = opts.time != null ? formationReference(u, opts.time) : null;
  const w = ref ? gestureFormationKnobs().weight : 0;
  const anchor = ref
    ? {
        x: ally.x * (1 - w) + ref.x * w,
        y: ally.y * (1 - w) + ref.y * w,
      }
    : ally;
  const bearingOx = ref?.x ?? u.x;
  const bearingOy = ref?.y ?? u.y;
  const base = Math.atan2(bearingOy - boss.y, bearingOx - boss.x);
  const bounds = raidPlayBounds();

  let best: { tx: number; ty: number } | null = null;
  let bestScore = -Infinity;
  for (const off of [
    0, 0.35, -0.35, 0.7, -0.7, 1.1, -1.1, 1.6, -1.6, Math.PI * 0.5, -Math.PI * 0.5,
  ]) {
    const ang = base + off;
    const px = boss.x + Math.cos(ang) * out;
    const py = boss.y + Math.sin(ang) * out;
    const g = resolveGoalInBounds(u, px, py);
    const dBoss = dist(g.tx, g.ty, boss.x, boss.y);
    if (dBoss < minDist * 0.85) continue;
    const dAnchor = dist(g.tx, g.ty, anchor.x, anchor.y);
    const edgeProx = Math.min(
      g.tx - bounds.xMin - u.radius,
      bounds.xMax - u.radius - g.tx,
      g.ty - bounds.yMin - u.radius,
      bounds.yMax - u.radius - g.ty,
    );
    // Away from boss, nearer formation/allies, strongly penalize wall lips.
    const lip = Math.min(48, Math.max(0, edgeProx));
    const lipPenalty = edgeProx < 28 ? (28 - Math.max(0, edgeProx)) * 1.15 : 0;
    const score =
      dBoss * 0.4 - dAnchor * 0.035 + lip * 0.85 - lipPenalty;
    if (score > bestScore) {
      bestScore = score;
      best = g;
    }
  }
  if (best) {
    return pullGoalOffLip(u, best, anchor);
  }

  // Fallback: radial + wall resolve (never raw past-bounds camp).
  const minRing = boss.radius + u.radius + 8;
  const radial = ringPointFromUnit(u, boss, out, minRing, maxRing, {
    time: opts.time,
  });
  return pullGoalOffLip(
    u,
    resolveGoalInBounds(u, radial.tx, radial.ty),
    anchor,
  );
}

/** Tank critical backpedal toward living healer (kit `retreatToHealer`). */
export function retreatToHealerGoal(
  u: RaidUnit,
  boss: RaidBoss,
  allies: readonly RaidUnit[],
  enemies: readonly RaidUnit[],
  time: number,
  stepPx: number,
  healerBias: number,
): { tx: number; ty: number } | null {
  const healer = allies.find((a) => unitIsStaffHealer(a));
  if (!healer) return null;
  const threat = retreatThreatPoint(u, boss, enemies, time);
  if (threat) {
    retreatDirBiased(
      u.x,
      u.y,
      threat.x,
      threat.y,
      healer.x,
      healer.y,
      healerBias,
      RETREAT_SCRATCH,
    );
  } else {
    const dx = healer.x - u.x;
    const dy = healer.y - u.y;
    const len = Math.hypot(dx, dy) || 1;
    RETREAT_SCRATCH.x = dx / len;
    RETREAT_SCRATCH.y = dy / len;
  }
  return resolveGoalInBounds(
    u,
    u.x + RETREAT_SCRATCH.x * stepPx,
    u.y + RETREAT_SCRATCH.y * stepPx,
  );
}

/**
 * Pick a support move goal that does not cross boss threat volumes.
 * Prefers tank backline / tangential approaches over a straight line through body.
 * `backlineHint` (adjutant-owned, facing-gated) replaces hard `tank.y + healerBackline`.
 */
export function pickHealerSupportGoal(
  u: RaidUnit,
  boss: RaidBoss,
  need: RaidUnit | null,
  tank: RaidUnit | null,
  time: number,
  stayBehindTank: boolean,
  enemies: readonly RaidUnit[] = [],
  includeBoss = true,
  backlineHint: { x: number; y: number } | null = null,
): { tx: number; ty: number } {
  const combat = combatTable();
  const vols = raidThreatVolumes(boss, time, u.radius, {
    enemies,
    includeBoss,
  });
  const steps = raidThreatSegmentSteps();
  const maxCost = raidThreatMaxAcceptCost();

  type Cand = { tx: number; ty: number; prefer: number };
  const cands: Cand[] = [];

  const tankBackline = (): { tx: number; ty: number } | null => {
    if (!tank) return null;
    if (backlineHint) {
      return resolveGoalInBounds(u, backlineHint.x, backlineHint.y);
    }
    const slotted = applySlot(tank.x, tank.y + combat.healerBackline, u);
    return resolveGoalInBounds(u, slotted.tx, slotted.ty);
  };

  if (stayBehindTank && tank) {
    const slotR = combat.formationSlotR.healer;
    const slottedBack = tankBackline()!;
    cands.push({
      tx: slottedBack.tx,
      ty: slottedBack.ty,
      prefer:
        need &&
        dist(slottedBack.tx, slottedBack.ty, need.x, need.y) <=
          u.attackRange + slotR
          ? 0
          : 2,
    });
  }

  if (need) {
    const slotted = applySlot(need.x, need.y + 30, u);
    cands.push({ tx: slotted.tx, ty: slotted.ty, prefer: 1 });
    // Tangential approaches relative to need↔threat (live boss or pack COM).
    let ax = boss.x;
    let ay = boss.y;
    if (!includeBoss) {
      let sx = 0;
      let sy = 0;
      let n = 0;
      for (const e of enemies) {
        if (e.team !== 1 || e.hp <= 0 || !e.combatArmed) continue;
        sx += e.x;
        sy += e.y;
        n += 1;
      }
      if (n > 0) {
        ax = sx / n;
        ay = sy / n;
      } else if (tank) {
        ax = tank.x;
        ay = tank.y - 40;
      }
    }
    const dx = need.x - ax;
    const dy = need.y - ay;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    for (const off of raidThreatTangentialOffsets()) {
      const tx = need.x + px * off;
      const ty = need.y + py * off;
      const healOk = dist(tx, ty, need.x, need.y) <= u.attackRange * 1.05;
      cands.push({ tx, ty, prefer: healOk ? 0.5 : 3 });
    }
    // Approach from tank side of need (halfway), then per-unit slot.
    if (tank) {
      const mid = applySlot(
        (tank.x + need.x) * 0.5,
        (tank.y + need.y) * 0.5,
        u,
      );
      cands.push({ tx: mid.tx, ty: mid.ty, prefer: 1.5 });
    }
  }

  // Low-threat ally/tank anchor when no heal need (never hardcode boss ring).
  if (cands.length === 0) {
    const back = tankBackline();
    if (back) return back;
    return { tx: u.x, ty: u.y };
  }

  let best: { tx: number; ty: number } | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  let bestSafe: { tx: number; ty: number } | null = null;
  let bestSafeScore = Number.POSITIVE_INFINITY;

  for (const c of cands) {
    const g = resolveGoalInBounds(u, c.tx, c.ty);
    const cost = segmentThreatCost(u.x, u.y, g.tx, g.ty, vols, steps);
    const score = cost + c.prefer * 4;
    if (score < bestScore) {
      bestScore = score;
      best = g;
    }
    if (cost <= maxCost && score < bestSafeScore) {
      bestSafeScore = score;
      bestSafe = g;
    }
  }

  if (bestSafe) return bestSafe;
  // All paths hot — stay on tank backline (or stand) rather than cross hot volumes.
  const back = tankBackline();
  if (back) return back;
  return best ?? { tx: u.x, ty: u.y };
}

// ── measure plant / in-range stand policy ───────────────────────────────────

/** Swing-stop when plantBlend above this (shared kit goal + tick motion). */
export const SWING_STOP_PLANT_BLEND = 0.15;

/**
 * Measure→plant directly: measure 0 → 1 (full plant), measure ≥50 → 0.
 * Independent of standRangeMul curve so kite-band tuning does not move plant.
 */
export function plantBlendFromMeasure(measure: number): number {
  return Math.min(1, Math.max(0, (50 - measure) / 50));
}

export function lerpPlantGoal(
  u: { x: number; y: number },
  kite: { tx: number; ty: number },
  plantBlend: number,
): { tx: number; ty: number } {
  if (plantBlend >= 1) return { tx: u.x, ty: u.y };
  if (plantBlend <= 0) return kite;
  return {
    tx: u.x + (1 - plantBlend) * (kite.tx - u.x),
    ty: u.y + (1 - plantBlend) * (kite.ty - u.y),
  };
}

/** In-reach stand deadzone — soft-sep must not restart micro flee↔close. */
export function inRangeStandDeadzone(
  curDist: number,
  stand: number,
  engageRing: number,
  holdEps: number,
): boolean {
  return curDist <= engageRing && Math.abs(curDist - stand) <= holdEps;
}

/**
 * When already in weapon reach, blend kite/ring goal toward feet by measure.
 * No-op when out of reach or plantBlend≈0.
 */
export function applyMeasurePlantGoal(
  u: { x: number; y: number },
  kite: { tx: number; ty: number },
  measure: number,
  inReach: boolean,
): { tx: number; ty: number } {
  if (!inReach) return kite;
  return lerpPlantGoal(u, kite, plantBlendFromMeasure(measure));
}
